import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, LinearProgress, Button, Tooltip, Chip, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, SelectChangeEvent, FormHelperText, Alert } from '@mui/material';
import { DataGridPro, DataGridProProps, GridRowId, GridRowParams } from '@mui/x-data-grid-pro';
import { GridColDef, GridToolbarColumnsButton } from '@mui/x-data-grid';
import { FileDownload, ErrorOutline, CheckCircleOutline, Visibility, HistoryOutlined, Close } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { truncationWarningText } from '../helper/truncationWarning';
import { saveAs } from 'file-saver';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { takeBatchReviewLoad } from '../helper/batchReviewPrefetch';
import { isSaveFailure } from '../helper/saveOutcome';
import { nextPanelSeed, PanelSeed, OptimisticWrite } from '../helper/panelReseed';
import { config } from '../Constants';
import {
    TAX_STATUS_OPTIONS, isLloydsInsurer, LLOYDS_UMR_LABEL,
    MAILING_ADDRESS_KEYS, RISK_ADDRESS_KEYS, ADDRESS_COMPONENT_KEYS, LEGACY_ADDRESS_KEYS,
    addressComponent, hasAddressComponents,
} from '../helper/filingFieldOptions';
import { writeXlsxFile } from '../helper/utils';
import {
    BATCH_REVIEW_EXPORT_HEADERS,
    buildBatchReviewExportRows,
    formatFilingDate,
} from '../helper/batchFilingExport';
import { WhiteColor } from '../helper/theme';
import formatPrice, { toMoneyNumber } from '../helper/currencyFormatter';
import { AppRoutes } from '../helper/appRoutes';
import { useExtractionStates } from '../helper/filingReviewStates';
import {
    BatchReviewFilterValues,
    clearedFilterValues,
    filterValuesOnClientChange,
    filterValuesOnStateChange,
    isAnyFilterActive,
} from '../helper/batchReviewFilters';
import { isScopeSettled } from '../helper/batchReviewLoadScope';
import { DocumentViewer, DocumentViewerItem } from '../components/Elements/DocumentViewer';
import { CoverageCodePicker } from '../components/Elements/CoverageCodePicker';
import { ClientFilingDefaultsPanel } from '../components/Elements/ClientFilingDefaultsPanel';
import { FilingEvidenceDialog } from '../components/Elements/FilingEvidenceDialog';
import { AddressFieldsEditor } from '../components/Elements/AddressFieldsEditor';
import {
    LIFECYCLE_TABS, LifecycleTab, RowFilingStatus, StateSubmitResult, SubmitBatchData,
    confirmedPolicyCount, deriveStateOutcome, escapeHtml, failureReasons, isEmptySuccess,
    isRowFilable, isRowInspectable, isRowProofBatchEligible, planProofBatch,
    matchesLifecycleTab, reportedCounts, rowFilingStatus, statusChipStyle,
    statusTooltip, tabTooltip,
    NO_BATCH_DATE, batchDateLabel, batchDateOf,
    blockerBandCopy, isRowSubmitted, isSubmittedStatus, lloydsConfirmCopy, reviewReasonLine,
    showAlreadySubmittedBand,
} from '../helper/batchFilingLifecycle';

interface PartnerOption {
    partnersId: number;
    customerName: string;
};

interface StateOption {
    stateId: number;
    stateName: string;
    postalAbbv?: string;
};

interface FieldException {
    field: string | null;
    label: string | null;
    severity: 'missing' | 'error' | 'warning';
    message: string;
};

interface ReadinessGap {
    key: string;
    label: string;
    note?: string;
};

interface ManualReviewItem {
    policyNumber?: string;
    kind: string;
    reason: string;
};

/** One declined admitted carrier from the Diligent Effort Affidavit (read-only summary). */
interface Declination {
    name: string;
    naic: string | null;
};

/** One reviewable field from the shared review envelope — same shape the (soon-to-be-retired)
 *  single-policy Filing Review screen renders. Ported here so every section of that screen (grouped
 *  fields, editing, provenance) lives in the batch expand-panel instead. */
type FilingFieldGroup = 'document' | 'policyRecord' | 'computed' | 'filer';
interface FilingField {
    key: string;
    label: string;
    group: FilingFieldGroup;
    value: string | number | null;
    detail?: string;
    source?: string;
    editable: boolean;
    type?: string;
    confidence?: number | null;
    sourcePage?: number | null;
    sourceQuote?: string | null;
    sourceDocument?: string | null;
    provenanceStatus?: string;
    /** Whether the selected state files this field. `false` → hidden from the filing detail panel;
     *  `true` / `'conditional'` → shown (and, for the document group, editable). */
    required?: boolean | 'conditional';
    /** Backend flag: this value is owned by the customer-policy DB record (writing-insurer NAIC, agency
     *  license), not the document — so when it's missing the "edit on customer page" link is shown and it
     *  is corrected upstream, never editable in place. Set only on those fields (Kelly, 2026-07-15). */
    resolveUpstream?: boolean;
};

interface BatchReviewRow {
    extractedDocumentDataId: number;
    customerPolicyId: number;
    partnersId: number;
    client: string;
    insuredName: string;
    stateId: number;
    state: string;
    extractionDate: string;
    policyNumber: string;
    transactionType: string;
    status: string;
    dueDate: string;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    policyEffectiveDate: string | null;
    policyExpirationDate: string | null;
    documentName: string;
    documentType: string;
    premium: number | null;
    tax: number | null;
    coverageCode: string | null;
    coverageCodeName: string | null;
    physicalRiskAddress: string | null;
    hasException: boolean;
    fieldExceptions: FieldException[];
    ready: boolean;
    requiredTotal: number;
    requiredReady: number;
    gaps: ReadinessGap[];
    blockers: string[];
    /** Field key(s) a blocker implicates (backend errorKeys) → the review UI outlines that exact field
     *  in red even when it isn't a counted readiness gap (e.g. DB-sourced writing insurer / NAIC / SLA
     *  broker). Optional: a state whose validator hasn't been tagged yet simply omits it. */
    blockerFieldKeys?: string[];
    warnings: string[];
    manualReview: ManualReviewItem[];
    /** Read-only list of the actual declined admitted carriers (from the Diligent Effort Affidavit),
     *  summarized under the "Admitted declinations" field. Empty when no affidavit was in the submission. */
    declinations: Declination[];
    /** The full per-field review list (document/policyRecord/computed/filer groups) — what the expand
     *  panel renders in place of the old single-policy Filing Review screen.
     *
     *  FETCHED ON EXPAND, not with the grid. It is ~40 entries per policy (each carrying a value, a
     *  source page, a confidence and a source quote) and measured 83.6% of the batch response's bytes,
     *  while the COLLAPSED grid renders none of it — see `fieldsLoaded`. Empty until then. */
    fields: FilingField[];
    /** Whether `fields` above has actually been loaded for this row, as opposed to being empty because
     *  nobody has opened it yet. The two are indistinguishable from `fields` alone, and conflating them
     *  would let the expand panel render "no fields" as though the policy genuinely had none, and let
     *  Save post an empty correction set over a real one.
     *
     *  Set true either by the lazy per-row fetch or, on an older internal-api that still inlines the
     *  list, by the batch response itself — so this screen works against both. */
    fieldsLoaded?: boolean;
    /** ---- Filing lifecycle (backend, optional) ----------------------------------------------
     *  Where this policy actually is in the filing lifecycle. `ready` above is only PRE-filing
     *  completeness, so without these a filed policy would sit under "Ready to File" forever. All
     *  three are optional: an older internal-api omits them and the screen falls back to
     *  completeness-only behaviour (see helper/batchFilingLifecycle). */
    filingStatus?: string | null;
    submissionNumber?: string | number | null;
    submittedAt?: string | null;
    /** WHY a filing failed, in the clearinghouse's own words (e.g. "Invalid SLIP User/Password
     *  specified."). Present only on rejected/error/conflict — a filer needs a next step, and a red chip
     *  on its own gives none. Null on success by design. Already credential-redacted server-side. */
    filingStatusReason?: string | null;
};

/**
 * How many rows the batch fetches in ONE request. The grid then pages client-side, so this is a hard
 * ceiling on everything derived from `policies`: the lifecycle tab counts, the premium and tax totals,
 * and "Download all". Past this many matching rows those figures describe only what was loaded.
 *
 * Kept as a named constant, and paired with the truncation banner below, because the failure mode is
 * SILENT — a truncated total looks exactly like a complete one, and a filer has no way to tell that a
 * premium figure is missing half the book.
 */
const BATCH_FETCH_SIZE = 500;

/** One selectable filing day, from GET /AutomaticFiling/filingBatchDates. Only days that actually have
 *  a batch are offered: an open calendar makes all 365 days a year look equally plausible and answers
 *  "nothing found" for almost all of them, which reads as a broken filter rather than an empty day. */
interface BatchDateOption {
    batchDate: string;
    policyCount: number;
}

/** Turn the API's integrity verdict into something a filer can act on.
 *
 *  ⚠️ `unanchored` is NOT a failure and must not be dressed as one: it means the artifact predates hash
 *  capture, so it is still the real transmitted document, just not provable. Calling that "corrupt"
 *  would cry wolf on every historical filing and teach whoever uses this screen to click through the
 *  warning that actually matters. `mismatch` is the one that matters. */
function integrityNotice(integrity: string, count: number, day: string):
        { severity: 'success' | 'warning' | 'error'; message: string } {
    if (integrity === 'mismatch') {
        return {
            severity: 'error',
            message: `The stored artifact for ${day} does NOT match the SHA-256 recorded when it was `
                + 'transmitted. Do not treat this file as evidence of what was filed; raise it before use.',
        };
    }
    if (integrity === 'missingBody') {
        return {
            severity: 'warning',
            message: `Part of the ${day} batch has no stored payload, so the download is incomplete.`,
        };
    }
    if (integrity === 'unanchored') {
        return {
            severity: 'warning',
            message: `Downloaded ${count} original submitted file(s) for ${day}. These predate integrity `
                + 'hashing, so they are the stored originals but cannot be verified against a transmit-time hash.',
        };
    }
    return {
        severity: 'success',
        message: `Downloaded ${count} original submitted file(s) for ${day}, each verified against the `
            + 'SHA-256 recorded at transmission.',
    };
}

const EDITABLE_FIELDS = ['coverageCode', 'premium', 'tax', 'physicalRiskAddress'] as const;

/** Same normal/hover blue as the "Search" button on the Filing Queue (Tabs.tsx) — a solid #2099d2
 *  that darkens to #176990 on hover — shared so every primary button on this screen matches it
 *  instead of picking its own blue. Use with variant="contained". */
const FILING_QUEUE_BLUE_BUTTON_SX = {
    textTransform: 'none' as const,
    fontWeight: 600,
    backgroundColor: '#2099d2',
    '&:hover': { backgroundColor: '#176990' },
};

/** Batch review returns each row's state as a display name (or 2-letter). The generic extraction
 *  trigger (POST /AutomaticFiling/extract) keys on the 2-letter code, so normalize before calling it.
 *
 *  ⚠️ THIS IS THE LAST-RESORT FALLBACK, NOT THE SOURCE OF TRUTH. The postal code should come from the
 *  capability registry (`states`, loaded from the API and covering every filing-enabled state); this map
 *  only covers the case where that lookup can't resolve a row. It previously held FOUR entries
 *  (TN/FL/CA/TX) — a leftover from when those were the live set — which was invisible only because the
 *  registry lookup normally wins. When a stale closure made that lookup miss every time, this map
 *  silently became the whole mechanism and Re-extract began posting full state NAMES for everything
 *  else. Kept complete so that if it is ever reached again it is correct rather than quietly wrong.
 *
 *  Lookup is case- and whitespace-insensitive: the display name arrives from the DB and has been seen
 *  with inconsistent casing. */
const STATE_TWO_LETTER: Record<string, string> = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
    colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
    hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
    kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
    massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
    montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
    oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
    virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
    // Non-state jurisdictions that file surplus lines.
    'district of columbia': 'DC', 'washington dc': 'DC', 'washington d.c.': 'DC',
    'puerto rico': 'PR', 'virgin islands': 'VI', 'u.s. virgin islands': 'VI',
    guam: 'GU', 'american samoa': 'AS', 'northern mariana islands': 'MP',
};
function toStateCode(state: string): string {
    if (!state) return state;
    const trimmed = state.trim();
    if (trimmed.length === 2) return trimmed.toUpperCase();
    return STATE_TWO_LETTER[trimmed.toLowerCase()] || trimmed;
}

// const initPartner: PartnerOption = { partnersId: 0, customerName: '' };
// const initState: StateOption = { stateId: 0, stateName: '' };

/** Best-effort exceptions from the backend aren't always attributable to a specific field (the
 *  underlying validators emit flat strings) — this looks up the ones that ARE, for cell highlighting. */
function exceptionsForField(row: BatchReviewRow, field: string): FieldException[] {
    return (row.fieldExceptions || []).filter((e) => e.field === field);
};

/** Human label for a manual-review flag's `kind` — same mapping as the single-policy Filing Review
 *  screen, so a batch row reads identically to what the filer sees after opening it. 
 */
function kindLabel(kind: string): string {
    switch (kind) {
        case 'lloyds_syndicate_confirm':
            return "Lloyd's Syndicate NAIC needed";
        case 'declinations_missing':
            return 'Declinations missing';
        case 'declination_naic_missing':
            return 'Declination carrier NAIC missing';
        case 'declinations_short':
            return 'Not enough declinations';
        case 'lloyds_naic_conflict':
            return 'Carrier NAIC mismatch';
        default:
            return kind;
    }
};

// ---------------------------------------------------------------------------
// Field-group rendering — ported from FilingReview.tsx (the single-policy screen this batch panel is
// replacing). Section headers, source labels, and formatting all match that screen so a filer sees the
// exact same thing whether they open a policy alone or expand it in the batch grid.
// ---------------------------------------------------------------------------

const GROUP_LABELS: Record<string, string> = {
    document: 'Extracted from document — correct here if wrong',
    policyRecord: 'Policy record',
    computed: 'Computed',
    filer: 'Licensee',
};

/** The one editable computed field: the coverage code is picked from a searchable list, not free-typed. */
function isCoverageField(field: FilingField): boolean {
    return /coverage/i.test(field.key) || /coverage/i.test(field.label);
}

/** Inline label shown above each filing-detail field. Drops a long trailing parenthetical from the
 *  writing-insurer-NAIC label (it wraps to two lines and makes the row taller than its siblings) — the
 *  full label stays available as a tooltip. Other labels are returned unchanged. */
function displayFieldLabel(field: FilingField): string {
    // UMR is a Lloyd's-only concept (Kelly, 2026-07-14): show its full plain-language name rather than
    // the terse backend "UMR (Lloyd's, v2.4)". The field is only rendered for Lloyd's insurers (see the
    // isLloydsInsurer gate in AreasToFixPanel), so the "Lloyd's" qualifier reads naturally here.
    if (field.key === 'umr') return LLOYDS_UMR_LABEL;
    if (/naic/i.test(field.key) || /insurer\s*naic/i.test(field.label)) {
        return field.label.replace(/\s*\(.*\)\s*$/, '').trim();
    }
    return field.label;
}

/* The effective "ready to file" verdict the whole screen uses is now `isRowFilable` in
 * helper/batchFilingLifecycle — the backend's completeness `ready` flag AND not already submitted.
 * (Previously a local isRowReady that only read `ready`, which is how a filed policy could stay under
 * "Ready to File" forever and be re-selected for a second submission.)
 * A reliable FE-side "declinations claimed but carriers not captured" guard still isn't possible: the
 * batch API doesn't surface the declined-carrier list per row, so that block lives in backend
 * readiness (see the internal-api readiness prompt). Single accessor so any future guard applies
 * everywhere — chip, tabs, counts, selection — at once. */

const SOURCE_LABELS: Record<string, string> = {
    document: 'Extracted Document',
    policyRecord: 'MISC Database',
    computed: 'Computed',
    filer: 'Licensee',
};

const GROUP_ORDER: FilingFieldGroup[] = ['document', 'policyRecord', 'computed', 'filer'];

function sourceText(field: FilingField): string | null {
    const base = field.source ? (SOURCE_LABELS[field.source] ?? field.source) : null;
    if (base && field.sourceDocument) return `${base} · ${field.sourceDocument}`;
    return base ?? field.detail ?? null;
}

function groupFilingFields(fields: FilingField[]): Record<string, FilingField[]> {
    const grouped: Record<string, FilingField[]> = {};
    for (const g of GROUP_ORDER) grouped[g] = [];
    for (const f of fields) {
        if (!grouped[f.group]) grouped[f.group] = [];
        grouped[f.group].push(f);
    }
    return grouped;
}

function formatFilingCurrency(value: string | number): string {
    const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

// formatFilingDate now lives in src/helper/batchFilingExport.ts and is imported above, so the grid's
// date columns and the spreadsheet share ONE formatter. They previously could not disagree only
// because the export did no formatting at all — which is the defect being fixed, not a guarantee.

/** Relative "time ago" for the extraction timestamp (EDD CreatedAt, stored UTC). An event-in-time, so
 *  it's shown relative to now (TZ-agnostic); the absolute value goes in the hover title in LOCAL time.
 *  Tells a filer whether the extraction is fresh — and confirms a Re-extract landed. */
function formatExtractedAgo(value: string | number): string {
    const d = new Date(String(value).trim());
    if (isNaN(d.getTime())) return '';
    const secs = Math.round((Date.now() - d.getTime()) / 1000);
    if (secs < 45) return 'just now';
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return `on ${formatFilingDate(value)}`;
}

function formatFilingFieldValue(field: FilingField): string {
    if (field.value === null || field.value === undefined || field.value === '') return '';
    if (field.type === 'date') return formatFilingDate(field.value);
    if (field.type === 'currency') return formatFilingCurrency(field.value);
    return String(field.value);
}

interface DropdownOption { label: string; value: string; }

const ELIGIBILITY_STATUS_OPTIONS: DropdownOption[] = [
    'Listed alien insurer',
    'Approved non-admitted insurer',
    'Eligible surplus lines insurer',
    'Other',
].map((opt) => ({ label: opt, value: opt }));

const ENTITY_TYPE_OPTIONS: DropdownOption[] = [
    'LLC',
    'Corporation',
    'Partnership',
    'Sole Proprietor',
    'Individual',
    'Trust',
    'Other',
].map((opt) => ({ label: opt, value: opt }));

const EXEMPT_COMMERCIAL_PURCHASER_OPTIONS: DropdownOption[] = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

/** Fields rendered as a Yes/No dropdown (the SMART Y/N flags). Keep in step with the dropdown dispatch
 *  in EditableFilingField. */
const YES_NO_FIELD_KEYS = ['exemptCommercialPurchaser', 'windstormExclusion'];

/** Normalize a Y/N-ish field value (1/0, '1'/'0', 'Y'/'N', 'true'/'false') to the Yes/No dropdown's
 *  'true' | 'false' — or '' when genuinely unset — so the boolean dropdowns (ECP, Windstorm) show the
 *  value regardless of how the backend encodes the flag (the TX adapter sends 1/0). */
function toYesNoValue(v: string): string {
    const s = String(v ?? '').trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'y' || s === 'yes') return 'true';
    if (s === '0' || s === 'false' || s === 'n' || s === 'no') return 'false';
    return '';
}

function ReadOnlyField({ field, isGap }: { field: FilingField; isGap?: boolean }) {
    const isEmpty = field.value === null || field.value === '' || field.value === undefined;
    const valueColor = isGap ? '#c0392b' : isEmpty ? '#aab4bb' : '#334155';
    return (
        <Box>
            <Typography sx={{ fontSize: '12px', color: isGap ? '#c0392b' : '#64748b', mb: 0.5, fontWeight: isGap ? 700 : 400 }}>
                {field.label}{field.required ? <span style={{ color: '#c0392b' }}> *</span> : ''}
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: valueColor, wordBreak: 'break-word' }}>
                {isEmpty ? '—' : formatFilingFieldValue(field)}
            </Typography>
            {sourceText(field) && (
                <Typography sx={{ fontSize: '11.5px', color: '#64748b', mt: 0.5 }}>
                    {sourceText(field)}
                </Typography>
            )}
        </Box>
    );
}

interface FieldProvenanceProps {
    field: FilingField;
    onViewDocument?: (page?: number | null) => void;
}

/** DOC-field provenance under the value: a blue "↗ view in document · p.N" deep-link + a small
 *  confidence % pill (green, amber when < 85%). Renders nothing for values-only fields (no page /
 *  confidence), so DB/CALC fields stay quiet — matching the artifact's per-field treatment. */
function FieldProvenance({ field, onViewDocument }: FieldProvenanceProps) {
    const isEmpty = field.value === null || field.value === undefined || String(field.value).trim() === '';
    if (isEmpty) return null;

    const hasConfidence = typeof field.confidence === 'number';
    const hasPage = field.sourcePage != null;
    if (!hasConfidence && !hasPage) return null;

    const low = hasConfidence && (field.confidence as number) < 0.85;
    const confidencePct = hasConfidence ? `${Math.round((field.confidence as number) * 100)}%` : null;

    return (
        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
            {hasPage && onViewDocument && (
                <Box component="button" title="Open the source document at this page"
                    onClick={() => onViewDocument(field.sourcePage)}
                    sx={{ fontSize: '10.5px', fontWeight: 700, color: '#15607a', background: 'none', border: 'none', p: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}
                >↗ view in document · p.{field.sourcePage}</Box>
            )}
            {hasPage && !onViewDocument && (
                <Box sx={{ fontSize: '10.5px', color: '#64748b' }}>source p.{field.sourcePage}</Box>
            )}
            {confidencePct && (
                <Box title="Extraction confidence (derived from where the value was found)"
                    sx={{ fontSize: '9.5px', fontWeight: 800, borderRadius: '4px', px: 0.625, py: '1px', lineHeight: 1.7,
                        backgroundColor: low ? '#fff4e0' : '#e6f4ea', color: low ? '#b26a00' : '#1e7e34' }}
                >{confidencePct}</Box>
            )}
        </Box>
    );
}

/** Collapsed, read-only carrier summary rendered under the "Admitted declinations" count. Collapsed by
 *  default; expands to a compact name · NAIC list. Shows an explanatory line (not a toggle) when the
 *  affidavit — and therefore the carrier list — wasn't in the submission. */
function DeclinationsSummary({ declinations }: { declinations: Declination[] }) {
    const [open, setOpen] = useState(false);

    if (!declinations.length) {
        return (
            <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', mt: 0.5 }}>
                No declined carriers captured — Diligent Effort Affidavit not in this submission.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 0.5 }}>
            <Box component="button" type="button" onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '11.5px', fontWeight: 700, color: '#15607a', '&:hover': { textDecoration: 'underline' } }}
            >
                {declinations.length} declined carrier{declinations.length === 1 ? '' : 's'} {open ? '▴' : '▾'}
            </Box>
            {open && (
                <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 2, rowGap: 0.5, alignItems: 'baseline' }}>
                    {declinations.map((d, i) => (
                        <React.Fragment key={i}>
                            <Typography sx={{ fontSize: '12px', color: '#334155', wordBreak: 'break-word' }}>{d.name}</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: d.naic ? '#5b6770' : '#aab4bb', fontStyle: d.naic ? 'normal' : 'italic', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {d.naic ? `NAIC ${d.naic}` : 'NAIC not captured'}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );
}

/** Small "edit on customer page ↗" link shown under a DB-sourced review field that's missing (writing-
 *  insurer NAIC, agency brokerage license). Those values come from the Add/Edit Customer Policy Details
 *  record and are corrected there — NOT editable in the filing area, since editing here would rewrite the
 *  DB record used elsewhere (Kelly, 2026-07-15). Navigates in-app, same round-trip the customer page uses
 *  to deep-link back into this batch. Renders nothing if the backend didn't supply a path. */
/** A blue "↗" link that opens this policy's Add/Edit Customer Policy Details record. Used both to fix a
 *  specific DB-sourced blocker (default label) and as a section-title backlink to the whole record
 *  (label="Open policy record ↗", inline). All the read-only "Policy record" values (premium, taxable
 *  fees, writing carrier/NAIC, dates) are corrected THERE, not on the document — e.g. a $0.00 taxable
 *  fee that's wrong on the record is fixed on that page and re-pulled. */
function EditOnCustomerPageLink({ path, label = 'Edit on customer page ↗', inline = false, title }: { path?: string | null; label?: string; inline?: boolean; title?: string }) {
    const navigate = useNavigate();
    if (!path) return null;
    return (
        <Box component="button" type="button"
            title={title || "Correct this on the customer's policy details page (it comes from the database, not the document)"}
            onClick={() => navigate(path)}
            sx={{ mt: inline ? 0 : 0.5, background: 'none', border: 'none', p: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '11px', fontWeight: 700, color: '#15607a', '&:hover': { textDecoration: 'underline' } }}
        >{label}</Box>
    );
}

interface EditableFilingFieldProps {
    field: FilingField;
    value: string;
    onChange: (key: string, val: string) => void;
    isLloydsNaicField?: boolean;
    /** Hard-required field that is a real counted gap → red "required" treatment. */
    missing?: boolean;
    /** `required: 'conditional'` optional confirm/override → subtle amber hint, never red. */
    conditional?: boolean;
}

/** Compact "editable box" (matches the artifact's .fval-ed): blue border, tight padding, no floating
 *  label — the label + source chip are rendered above by the field item. Red only for a hard-required
 *  gap; amber (not red) for an optional conditional confirm/override or a Lloyd's NAIC still to review.
 *  Same input-type dispatch as the retiring single-policy screen (dropdown / number / multiline / text). */
function EditableFilingField({ field, value, onChange, isLloydsNaicField, missing, conditional }: EditableFilingFieldProps) {
    const needsReview = isLloydsNaicField || (field.key === 'writingInsurerNaicDoc' && !value);
    const isMissing = Boolean(missing) && !value;
    const isConditional = Boolean(conditional) && !value;
    const amber = isConditional || (needsReview && !value);
    const borderColor = isMissing ? '#d32f2f' : (amber ? '#e6c78c' : '#2099d2');
    const bgColor = isMissing ? 'rgba(211,47,47,0.06)' : (amber ? '#fffaf2' : WhiteColor);

    const boxSx = {
        '& .MuiInputBase-input': { fontSize: '14px', padding: '6px 9px' },
        '& .MuiInputBase-inputMultiline': { padding: 0 },
        '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: bgColor },
        '& .MuiOutlinedInput-notchedOutline': { borderColor },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor },
    } as const;

    const pencil = <Box component="span" sx={{ color: '#15607a', fontSize: '12px', pl: 0.5 }}>✎</Box>;

    if (field.key === 'eligibilityStatus' || field.key === 'entityType' || field.key === 'taxStatus' || YES_NO_FIELD_KEYS.includes(field.key)) {
        const isYesNo = YES_NO_FIELD_KEYS.includes(field.key);
        const options = field.key === 'eligibilityStatus' ? ELIGIBILITY_STATUS_OPTIONS
            : field.key === 'entityType' ? ENTITY_TYPE_OPTIONS
            : field.key === 'taxStatus' ? TAX_STATUS_OPTIONS
            : EXEMPT_COMMERCIAL_PURCHASER_OPTIONS;   // Yes/No (true/false) — shared by ECP + Windstorm
        // The Yes/No flags arrive as 1/0 from the backend; normalize to the option value so the
        // dropdown reflects the stored/defaulted value (blank showed even when set before this).
        const selectValue = isYesNo ? toYesNoValue(value) : value;
        return (
            <FormControl fullWidth size="small" sx={boxSx}>
                <Select value={selectValue} displayEmpty
                    onChange={(e: SelectChangeEvent<string>) => onChange(field.key, e.target.value)}
                    sx={{ fontSize: '14px' }}
                >
                    {options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '14px' }}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    if (field.key === 'declinationCount') {
        return (
            <TextField fullWidth size="small" type="number"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value)}
                InputProps={{ inputProps: { min: 0 }, endAdornment: pencil }}
                sx={boxSx}
            />
        );
    }

    if (field.key === 'descriptionOfRisk') {
        return (
            <TextField fullWidth size="small" multiline minRows={2}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(field.key, e.target.value)}
                sx={{ ...boxSx, '& .MuiOutlinedInput-root': { ...boxSx['& .MuiOutlinedInput-root'], padding: '6px 9px' } }}
            />
        );
    }

    const isNaicDoc = field.key === 'writingInsurerNaicDoc';
    const placeholder = isNaicDoc && !value ? 'Enter syndicate-specific NAIC (e.g. AA1122000)'
        : isMissing ? '— required'
        : isConditional ? 'Enter to override / confirm'
        : undefined;
    return (
        <TextField fullWidth size="small"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value)}
            placeholder={placeholder}
            InputProps={{ endAdornment: pencil }}
            sx={boxSx}
        />
    );
}

// ---------------------------------------------------------------------------
// Filing plan + outcome reporting
//
// The screen files EXACTLY the checked rows, grouped by each row's own state, because the submit
// endpoint is per-state but MISC files across many states in one action. Everything below is the
// plan the filer confirms and the truthful per-state report they get back.
// ---------------------------------------------------------------------------

interface FilingStateGroup {
    /** Route segment for POST /AutomaticFiling/{segment}/submit-batch. */
    segment: string;
    /** Display name as it appears on the rows. */
    stateName: string;
    rows: BatchReviewRow[];
}

interface FilingPlan {
    groups: FilingStateGroup[];
    /** Selected rows whose state has no filing route — surfaced in the confirm dialog, never dropped
     *  silently. */
    unresolved: BatchReviewRow[];
    /** Extra selected rows that resolve to a customerPolicyId already in the plan (a policy with more
     *  than one extraction row). Collapsed to one submission each, and reported so the count in the
     *  dialog reconciles with the number of checkboxes the filer ticked. */
    duplicates: BatchReviewRow[];
    /** Distinct clients the routable selection spans — more than one needs an explicit warning: MISC
     *  treats filing one client's policies inside another client's submission as a privacy breach. */
    clients: string[];
}

/** Short human handle for a row in a dialog list — insured + policy number, falling back to ids. */
function describeRow(row: BatchReviewRow): string {
    const parts = [row.insuredName, row.policyNumber].filter(Boolean);
    if (parts.length) return parts.join(' · ');
    return `Policy #${row.customerPolicyId}`;
}

const OUTCOME_HEADLINE: Record<StateSubmitResult['outcome'], string> = {
    filed: 'Filed',
    queued: 'Submitted — awaiting state approval',
    rejected: 'Rejected — nothing was filed',
    conflict: 'Conflict — nothing was filed',
    error: 'Error — the outcome is unconfirmed',
    transport: 'Error — the outcome is unconfirmed',
    manual: 'Manual upload — a batch file will be downloaded',
};

const OUTCOME_COLOR: Record<StateSubmitResult['outcome'], string> = {
    filed: '#1e7e34',
    queued: '#1565c0',
    rejected: '#c62828',
    conflict: '#c62828',
    error: '#b26a00',
    transport: '#b26a00',
    manual: '#334155',
};

function bulletList(items: string[]): string {
    return `<ul style="margin:4px 0 0 18px;padding:0">${items.map((i) => `<li style="margin-bottom:3px">${escapeHtml(i)}</li>`).join('')}</ul>`;
}

/** One state's block in the aggregate dialog. Filed / queued / rejected / conflict / unconfirmed are
 *  rendered as distinct outcomes — a `queued` submission is NEVER reported as filed. */
function renderStateResultHtml(result: StateSubmitResult, rowByPolicyId: Map<number, BatchReviewRow>): string {
    const { data } = result;
    // `null` = the backend reported no count at all; `0` = it reported none taken. These are NOT the
    // same and must not collapse: an empty `filed` array previously fell through an `||` chain to the
    // REQUESTED count, so a batch where everything was blocked announced itself as fully filed.
    const { filed: filedCount, queued: queuedCount } = reportedCounts(data);
    const confirmedCount = confirmedPolicyCount(data);
    const confirmation = data.submissionNumber
        ? `<div>Confirmation #: <b>${escapeHtml(data.submissionNumber)}</b>${data.submissionStatus ? ` · state status: ${escapeHtml(data.submissionStatus)}` : ''}</div>`
        : '';
    /** Renders a count the backend may not have given us, rather than inventing one. */
    const countText = (n: number | null) => (n === null ? 'An unreported number of' : `<b>${escapeHtml(n)}</b>`);

    let detail = '';
    switch (result.outcome) {
        case 'filed':
            detail = confirmedCount === 0
                // Nominally accepted, but the state took nothing — everything was blocked or dropped.
                // Say so plainly; the blocked/dropped sections below carry the reasons.
                ? `<div><b>No policies were accepted.</b> The state returned a success but reported nothing filed — see below.</div>${confirmation}`
                : `<div>${countText(filedCount ?? confirmedCount)} policy(ies) accepted by the state.</div>${confirmation}`;
            break;
        case 'queued':
            detail = confirmedCount === 0
                ? `<div><b>No policies were submitted.</b> The state returned a success but reported nothing queued — see below.</div>${confirmation}`
                : `<div>${countText(queuedCount ?? confirmedCount)} policy(ies) submitted. The state has not approved them yet — they stay under “Awaiting state” until it does.</div>${confirmation}${
                    filedCount ? `<div>${escapeHtml(filedCount)} of them were accepted outright.</div>` : ''}`;
            break;
        case 'rejected':
            detail = `<div>The clearinghouse rejected this batch:</div>${bulletList(result.errors)}`;
            break;
        case 'conflict':
            detail = `<div>This batch conflicts with a submission the state already has. Reconcile it before retrying:</div>${bulletList(result.errors)}`;
            break;
        case 'error':
        case 'transport':
            detail = `<div><b>The submission may still be in flight.</b> The reservation for these ${result.requestedIds.length} policy(ies) was kept so they cannot be filed twice — reconcile this batch with the state before retrying.</div>${bulletList(result.errors)}`;
            break;
        case 'manual':
            detail = '<div>Live API filing is not enabled for this state yet, so the batch file is being built for you to upload in the state portal.</div>';
            break;
    }

    const blocked = Array.isArray(data.blocked) && data.blocked.length
        ? `<div style="margin-top:4px;color:#c62828"><b>Blocked before sending</b>${bulletList(data.blocked.map((b) => {
            const who = b.clientName || rowByPolicyId.get(b.customerPolicyId)?.client || '';
            const label = rowByPolicyId.get(b.customerPolicyId);
            const name = label ? describeRow(label) : `Policy #${b.customerPolicyId}`;
            return `${name}${who ? ` (${who})` : ''}: ${b.reason || 'no reason given'}`;
        }))}</div>`
        : '';

    const dropped = Array.isArray(data.dropped) && data.dropped.length
        ? `<div style="margin-top:4px;color:#c62828"><b>Held back and NOT filed</b>${bulletList(data.dropped.map((d) => {
            const label = rowByPolicyId.get(d.customerPolicyId);
            const name = label ? describeRow(label) : `Policy #${d.customerPolicyId}`;
            return `${name}: ${d.reason || 'no reason given'}`;
        }))}</div>`
        : '';

    // Informational messages are worth showing even on a success, but not when they were already used
    // as the failure reasons.
    const succeeded = result.outcome === 'filed' || result.outcome === 'queued' || result.outcome === 'manual';
    const messages = succeeded && Array.isArray(data.messages) && data.messages.length
        ? `<div style="margin-top:4px;color:#5b6770">${bulletList(data.messages)}</div>`
        : '';

    return `<div style="margin-bottom:12px;padding-left:10px;border-left:3px solid ${OUTCOME_COLOR[result.outcome]}">
        <div style="font-weight:700;color:#0f172a">${escapeHtml(result.stateName)} — <span style="color:${OUTCOME_COLOR[result.outcome]}">${OUTCOME_HEADLINE[result.outcome]}</span></div>
        ${detail}${blocked}${dropped}${messages}
    </div>`;
}

/** Trailing note for rows the plan deliberately did not send. */
function renderHeldBackHtml(plan: FilingPlan): string {
    const parts: string[] = [];
    if (plan.unresolved.length) {
        parts.push(`<div style="margin-top:4px"><b>Not sent — no filing state</b>${bulletList(plan.unresolved.map((r) => `${describeRow(r)} — state ${r.state || 'not set'}`))}</div>`);
    }
    if (plan.duplicates.length) {
        parts.push(`<div style="margin-top:4px"><b>Collapsed — another extraction row for a policy already in this batch</b>${bulletList(plan.duplicates.map(describeRow))}</div>`);
    }
    if (!parts.length) return '';
    return `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e2e8f0;color:#c62828">${parts.join('')}</div>`;
}

/** Aggregate title + icon. Deliberately conservative: any failure downgrades the whole dialog, and a
 *  queue is reported as a submission awaiting approval rather than as a filing.
 *
 *  An "empty success" — a filed/queued outcome whose reported count is a hard zero, i.e. everything was
 *  blocked or dropped — counts as a FAILURE here. The title must never read "Filed to the state" over a
 *  body that says nothing was filed. */
function summarizeFilingResults(results: StateSubmitResult[]): { icon: 'success' | 'info' | 'warning' | 'error'; title: string } {
    if (!results.length) return { icon: 'info', title: 'Nothing was submitted' };
    const isFailure = (r: StateSubmitResult) =>
        r.outcome === 'rejected' || r.outcome === 'conflict' || r.outcome === 'error' || r.outcome === 'transport'
        || isEmptySuccess(r);
    const failed = results.filter(isFailure);
    const succeeded = results.filter((r) => (r.outcome === 'filed' || r.outcome === 'queued') && !isEmptySuccess(r));
    const manual = results.filter((r) => r.outcome === 'manual');

    if (failed.length && !succeeded.length && !manual.length) {
        return { icon: 'error', title: results.length === 1 ? 'Filing not submitted' : 'Nothing was filed' };
    }
    if (failed.length) return { icon: 'warning', title: 'Filed with problems' };
    if (!succeeded.length && manual.length) {
        return { icon: 'info', title: manual.length === 1 ? 'Manual upload for this state' : 'Manual upload for these states' };
    }
    if (succeeded.some((r) => r.outcome === 'queued')) {
        return { icon: 'info', title: 'Submitted — awaiting state approval' };
    }
    return { icon: 'success', title: results.length === 1 ? 'Filed to the state' : 'Filed to the states' };
}

/** Filing LIFECYCLE tabs driving `visiblePolicies` -- Not ready / Ready to file / Awaiting state /
 *  Filed / Needs attention / All. The old three tabs were all pre-filing completeness, so a policy
 *  that had already been filed stayed under "Ready to File" forever. Same Tabs-on-desktop /
 *  Select-on-mobile pattern as ReportTag.tsx's queue tabs, rendered as the grid's own Toolbar so it
 *  sits right above the rows it filters. Hidden until the batch has actually loaded.
 *
 *  DECLARED AT MODULE SCOPE, and it has to stay here. It used to be a nested `function` inside
 *  BatchFilingReview's body, which meant React saw a BRAND-NEW component type on every single render
 *  of the page. A new type is not a re-render, it is a REMOUNT: DataGridPro tore the toolbar's whole
 *  subtree down and rebuilt it each time, so the tab strip lost its DOM identity -- and with it focus,
 *  the scroll offset of the scrollable tab strip, and any open mobile Select -- whenever anything at
 *  all changed upstream, down to a single keystroke in a filter box. Hoisting it fixes the identity;
 *  the state it used to close over now arrives as props through the grid's `componentsProps.toolbar`
 *  (v5's name for what later versions call `slotProps`), which is the supported way to feed a slot
 *  component without redefining it.
 */
interface FilingStatusToolbarProps {
    /** The load LATCH, not `policiesLoaded`: the tab strip must not vanish and re-appear every time a
     *  filter is changed. See the latch's declaration for why these are two different facts. */
    hasLoadedOnce: boolean;
    /**
     * Per-tab counts, ALREADY MEMOISED BY THE PAGE on `policies`.
     *
     * ⚠️ PASSED IN, NOT RECOMPUTED HERE. This toolbar re-renders on every pass — including every
     * keystroke in the Insured Name / Policy Number boxes — and counting is six passes over the whole
     * batch. Taking `policies` and filtering it here would redo all of that per keystroke and quietly
     * undo the memo the page keeps for exactly this reason. Each count describes the set behind that
     * tab, i.e. the whole batch, not the visible page.
     */
    counts: Array<(typeof LIFECYCLE_TABS)[number] & { count: number }>;
    filingStatusFilter: LifecycleTab;
    setFilingStatusFilter: (tab: LifecycleTab) => void;
    /** Batch verification totals, already scoped to selection-or-view by the page's `totals` memo. */
    totals: { totalPremium: number; totalTax: number; totalsScopedToSelection: boolean; selectedCount: number };
    /**
     * ⚠️ NOT the same fact as `hasLoadedOnce`, and every FIGURE here depends on this one, not that one.
     * `hasLoadedOnce` is a latch that keeps the toolbar mounted once a batch has ever landed, so the
     * filer never loses the controls needed to retry. `policiesLoaded` means "the counts and totals
     * below describe the rows currently on screen", which is false while a load is in flight AND after
     * one has failed. In both of those states a number must render as an em dash: showing "0" or a
     * stale total is a CLAIM ABOUT THE QUEUE, and it is a false one -- it is how a filer ends up
     * reporting a premium/tax figure that was never computed for the set they are looking at.
     */
    policiesLoaded: boolean;
}

function FilingStatusToolbar({ hasLoadedOnce, counts, filingStatusFilter, setFilingStatusFilter, totals, policiesLoaded }: FilingStatusToolbarProps) {
    // ⚠️ NO EARLY RETURN HERE, DELIBERATELY. The tab strip and the columns control need no rows to be
    // correct, so withholding them for a ~3s request made the whole screen look broken for the whole of
    // it. Only the FIGURES wait on a batch, and they wait in `renderBatchNumbers` below. Restoring an
    // `if (!hasLoadedOnce) return null` up here would silently undo the first-paint behaviour this
    // toolbar exists to provide -- `batchFilingReviewFirstPaint.test.ts` pins that.
    // Same rule as the count chips: a figure the current result set cannot support renders as an em
    // dash, never as a formatted 0.00. See `policiesLoaded` on the props for why that matters here.
    const figure = (value: number) => (policiesLoaded ? formatPrice(value) : '—');
    const countChip = (n: number, bg: string, fg: string) => (
        <Box component="span" sx={{ ml: 0.75, fontSize: '11px', fontWeight: 700, px: 0.75, py: '1px', borderRadius: '999px', backgroundColor: bg, color: fg, lineHeight: 1.6 }}>{policiesLoaded ? n : '—'}</Box>
    );
    /**
     * The premium and tax totals — the only figures up here that cannot be computed without a batch.
     *
     * ⚠️ TWO DIFFERENT KINDS OF "NO NUMBER", AND THEY LOOK DIFFERENT.
     *   * No load has finished at all → nothing here, not even the labels. There is nothing to caption.
     *   * A load is in flight, or the last one FAILED → the labels stay and the figures read "—".
     * Neither may render "$0.00": that is a claim about the queue, and while the request is in flight
     * or after it failed it is a false one. Rendering it is how a filer ends up reporting a total that
     * was never computed. The rest of the toolbar around this does not wait for either.
     *
     * A plain function rather than a component: rendered as <BatchNumbers /> it would get a fresh
     * identity on every render and remount its subtree each time.
     */
    function renderBatchNumbers() {
        if (!hasLoadedOnce) return null;
        return (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'stretch', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10.5px', color: '#5b6770', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.1 }}>
                        {totals.totalsScopedToSelection ? `Total Premium (${totals.selectedCount} selected)` : 'Total Premium'}
                    </Typography>
                    <Typography sx={{ fontSize: '20px', color: '#132D3D', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>{figure(totals.totalPremium)}</Typography>
                </Box>
                <Box sx={{ width: '1px', backgroundColor: '#e2e8f0', my: 0.25 }} />
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10.5px', color: '#5b6770', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.1 }}>
                        {totals.totalsScopedToSelection ? `Total Tax (${totals.selectedCount} selected)` : 'Total Tax'}
                    </Typography>
                    <Typography sx={{ fontSize: '20px', color: '#132D3D', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>{figure(totals.totalTax)}</Typography>
                </Box>
            </Box>
        );
    }

    const tabIcon = (tab: LifecycleTab) => {
        if (tab === 'ready' || tab === 'filed') return <CheckCircleOutline fontSize="small" />;
        if (tab === 'notReady' || tab === 'needsAttention') return <ErrorOutline fontSize="small" />;
        return undefined;
    };
    return (
        <div className="p-2 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <Tabs value={filingStatusFilter}
                onChange={(_, newValue) => setFilingStatusFilter(newValue as LifecycleTab)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Filing lifecycle tabs"
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    minHeight: 48,
                    '& .MuiButtonBase-root': { minHeight: '0px' },
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem', minWidth: 0, px: 1.5 },
                    '& .Mui-selected': { color: '#2099d2' },
                    '& .MuiTabs-indicator': { backgroundColor: '#2099d2', height: 3, borderRadius: '3px 3px 0 0' },
                }}
            >
                {/* Every QUEUE tab carries the same plain-language hover as the statuses it holds
                    (checklist #3 asks for tooltips on statuses AND queues). Tooltip wraps the Tab
                    rather than being passed as a Tab prop so MUI still forwards the tab's own
                    click/aria wiring. */}
                {counts.map((t) => (
                    <Tooltip key={t.value} title={<span style={{ whiteSpace: 'pre-line' }}>{tabTooltip(t.value)}</span>}>
                        <Tab value={t.value}
                            icon={tabIcon(t.value)} iconPosition="start"
                            label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{t.label}{countChip(t.count, t.countBg, t.countFg)}</Box>} />
                    </Tooltip>
                ))}
            </Tabs>
            <FormControl size="small"
                sx={{
                    display: { xs: 'block', md: 'none' },
                    minWidth: 180,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                    '& .MuiSelect-select': { fontWeight: 600, color: '#2099d2', py: 0.9 },
                }}
            >
                <Select value={filingStatusFilter}
                    onChange={(e) => setFilingStatusFilter(e.target.value as LifecycleTab)}
                    displayEmpty
                    sx={{ textTransform: 'none', fontSize: '0.9375rem' }}
                >
                    {counts.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label} ({policiesLoaded ? t.count : '—'})</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Batch verification totals (checklist #4): prominent so the filer can confirm total
                    premium + tax match what they expect to report, at a glance, before submitting.
                    The label names the SET being totalled — without it, checking one row makes the
                    figure drop and nothing on screen explains why. */}
                {renderBatchNumbers()}
                <GridToolbarColumnsButton />
            </Box>
        </div>
    );
};

export function BatchFilingReview() {
    const { user } = useAuth();
    const token = user?.token;
    // The expand panel's action handlers are captured once (getDetailPanelContent has [] deps), so read
    // the bearer from a ref kept current — otherwise a mid-session token refresh would 401 Re-extract /
    // Download / Save against the stale first-render token.
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);
    const navigate = useNavigate();
    // `extractionStates` is only read to tell "this state isn't filing-enabled" apart from "the state
    // list hasn't loaded yet" when a selected row can't be routed — two very different messages.
    const { stateSegment, states: extractionStates } = useExtractionStates();
    // `stateSegment` is now memoized on the state map (see useExtractionStates), but it is still a NEW
    // identity on the render where the fetched state list first lands — and the expand panel's [] -deps
    // callback captured the version from the FIRST render, i.e. the one closed over an EMPTY map, which
    // returns null for every state forever. That silently disabled the panel's per-policy Download
    // entirely (it guards on `if (!segment) return`). Same fix as tokenRef: the ref identity is stable
    // and `.current` is refreshed each render, so reading through it at click time gets the real
    // resolver. Memoizing the resolver does not remove the need for this ref; it only means the ref
    // now stops re-pointing once the states have loaded.
    const stateSegmentRef = useRef(stateSegment);
    useEffect(() => { stateSegmentRef.current = stateSegment; });
    const [searchParams, setSearchParams] = useSearchParams();
    // Deep-link support: "View Extraction Details" elsewhere in the app links here with
    // ?CustomerPolicyId=<id> (e.g. AddEditCustomerPolicyDetails.tsx). Pre-fills the Client/State
    // dropdowns to that policy's batch, then auto-expands and scrolls to the specific row once it loads.
    const deepLinkCustomerPolicyId = searchParams.get('CustomerPolicyId') || searchParams.get('customerPolicyId');
    // Explicit client/state preselect — used to round-trip the selection when jumping to the Client
    // Filing Defaults page and back (so the filer returns to the same batch they left).
    const deepLinkPartnersId = searchParams.get('partnersId');
    const deepLinkStateId = searchParams.get('stateId');
    const [deepLinkPrefillAttempted, setDeepLinkPrefillAttempted] = useState(false);
    /** The deep-linked policy once its lookup has finished: `undefined` = still in flight, `null` = the
     *  lookup finished and produced nothing (not found, or it failed). The two must stay distinct —
     *  the apply effect waits on the first and gives up on the second. */
    const [deepLinkPolicy, setDeepLinkPolicy] = useState<any | undefined>(undefined);
    /**
     * WHAT THE URL PROMISED WHEN THIS SCREEN WAS OPENED — captured once, on arrival.
     *
     * Deliberately a snapshot rather than a live read of `searchParams`: the URL-sync effect below
     * WRITES partnersId/stateId back as the filer picks them, so a live read would start claiming a
     * preselect is pending every time someone chooses a client. The question this answers is only ever
     * about arrival: "is a narrower scope on its way, so the unscoped load should be skipped?"
     */
    const arrivalScopeRef = useRef({
        hasPartnerParam: Boolean(deepLinkPartnersId),
        hasStateParam: Boolean(deepLinkStateId),
        hasPolicyParam: Boolean(deepLinkCustomerPolicyId),
    });
    // The target row's grid id (ExtractedDocumentDataId), captured from the same lookup that resolves
    // client/state — so once the batch loads we know exactly which row to expand without re-searching.
    const [deepLinkTargetRowId, setDeepLinkTargetRowId] = useState<number | null>(null);

    // Paging matches the Ready-to-File / Outstanding Filings queues (50/page) so filers get the screen
    // they are already trained on (Jerry, punch-list item 3). Deliberately CLIENT-side: the batch loads
    // every row up front because the Ready / Not-ready tab counts, the premium+tax totals and the
    // "File policies" bulk action are all computed over the WHOLE set (readiness comes from building
    // each policy's review, not from a column, so SQL cannot count or filter it). Server-side paging
    // would silently scope those to the current page -- including which policies get FILED.
    const [pageSize, setPageSize] = useState<number>(50);
    const [page, setPage] = useState<number>(0);
    // Row the deep-link should scroll to once it is actually on the rendered page.
    const [deepLinkScrollRowId, setDeepLinkScrollRowId] = useState<number | null>(null);

    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [states, setStates] = useState<StateOption[]>([]);
    // Same hazard as tokenRef above, and it had the same consequence. The expand panel's handlers are
    // captured ONCE (getDetailPanelContent has [] deps), so anything they read must come from a ref.
    // `states` is fetched asynchronously and is still [] on first render, so a handler closing over it
    // could never resolve a postal code — Re-extract's registry lookup silently missed EVERY time and
    // fell through to the hardcoded name→code map below, posting a full state name ("New Jersey")
    // instead of a code ("NJ") for any state that map didn't happen to list. Read through the ref so
    // the capability registry — which covers every filing-enabled state — is actually consulted.
    const statesRef = useRef<StateOption[]>([]);
    useEffect(() => { statesRef.current = states; }, [states]);
    const [selectedPartner, setSelectedPartner] = useState<PartnerOption | null>(null);
    const [selectedState, setSelectedState] = useState<StateOption | null>(null);
    // "The option list request has SETTLED", success or failure — not "the list has rows". The
    // preselects below hang off these rather than off `length > 0`, so a client list that comes back
    // empty (or fails) still releases the scope gate instead of leaving the screen waiting forever.
    const [partnersSettled, setPartnersSettled] = useState(false);
    const [statesSettled, setStatesSettled] = useState(false);
    // Each URL-driven preselect has run to a conclusion. See ../helper/batchReviewLoadScope: the batch
    // fetch waits on these so the expensive unscoped request is never issued ahead of a scoped one.
    const [partnerScopeResolved, setPartnerScopeResolved] = useState(false);
    const [stateScopeResolved, setStateScopeResolved] = useState(false);
    const [policyScopeResolved, setPolicyScopeResolved] = useState(false);
    const scopeSettled = isScopeSettled({
        ...arrivalScopeRef.current,
        partnerResolved: partnerScopeResolved,
        stateResolved: stateScopeResolved,
        policyResolved: policyScopeResolved,
    });
    // THE SCOPE THE FETCHES KEY ON — plain numbers, never the option objects.
    //
    // The load effects used to depend on `selectedPartner` / `selectedState` directly, and React
    // compares dependencies by REFERENCE. The deep-link lookup builds its client as a fresh
    // `{ partnersId, customerName }` literal, so re-selecting the client that was already selected
    // produced a new object, a changed dependency and a fourth full batch fetch for a scope that had
    // not changed at all. Ids cannot do that.
    const scopePartnersId = selectedPartner?.partnersId ?? 0;
    const scopeStateId = selectedState?.stateId ?? 0;

    const [policies, setPolicies] = useState<BatchReviewRow[]>([]);
    const [policiesLoaded, setPoliciesLoaded] = useState(false);
    /**
     * "A batch has landed at least once", as opposed to `policiesLoaded`, which means "the CURRENT
     * result set has settled".
     *
     * ⚠️ THESE ARE TWO DIFFERENT FACTS AND ONE FLAG WAS CARRYING BOTH. The filter bar, the tab strip and
     * the empty-state were gated on `policiesLoaded`, which is reset to false at the START of every
     * load. So choosing a Client, a State or a Batch Date re-fired the load effect and unmounted the
     * Insured Name, Policy Number and Batch Date controls for the whole round trip — the Batch Date
     * Select tore down its own open popover the instant you picked an option. QA reported it as "three
     * of the filters at the top disappear", which is exactly what it is.
     *
     * This latch is set once the first batch lands and is never reset by a later load, so the controls
     * stay mounted across refetches. `policiesLoaded` keeps its narrower meaning for the deep-link
     * effect below, which genuinely needs "the rows on screen are the ones this response describes" —
     * pointing that at the latch would let a deep link act on stale rows.
     */
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    // How many rows the server says MATCH the filter, which is not the same as how many we loaded. The
    // batch is fetched as a single page of BATCH_FETCH_SIZE and paged client-side, so past that cap the
    // tab counts, the premium/tax totals and "Download all" all describe a TRUNCATED set while looking
    // authoritative. The backend has always returned totalRecords; the grid simply never read it.
    const [totalAvailable, setTotalAvailable] = useState<number | null>(null);
    // Bumped to force the batch to reload in place (e.g. after a per-policy Re-extract) without the
    // filer re-picking client/state — the load effect includes it in its dependency list.
    const [reloadNonce, setReloadNonce] = useState(0);
    const [filingStatusFilter, setFilingStatusFilter] = useState<LifecycleTab>('ready');
    // Quick text filters (Kelly, 2026-07-14) — narrow the loaded batch by insured name / policy number,
    // matching the Filing Queue's own column filters. Client-side contains match over the loaded rows.
    const [insuredNameFilter, setInsuredNameFilter] = useState('');
    const [policyNumberFilter, setPolicyNumberFilter] = useState('');
    // BATCH DATE (Asana 1217932622794487) — pick a filing day, see the policies that belonged to that
    // night's batch, download that batch. '' = every day; NO_BATCH_DATE = only the rows with no
    // recorded batch date.
    //
    // ⚠️ Applied SERVER-side, unlike the two text filters above. Those narrow rows already loaded; this
    // one narrows what is loaded, because the whole point of picking a historical day is not to fetch
    // the current queue first. It also means the tab counts and totals describe THAT BATCH, which is
    // what a filer reviewing a past day means by them.
    const [batchDateFilter, setBatchDateFilter] = useState('');
    const [batchDateOptions, setBatchDateOptions] = useState<BatchDateOption[]>([]);
    const [downloadingBatch, setDownloadingBatch] = useState(false);
    const [batchDownloadNotice, setBatchDownloadNotice] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
    /**
     * THE RETAINED ARTIFACTS FOR THE SELECTED BATCH DAY, OR AN EMPTY LIST IF THERE ARE NONE.
     * `null` means "not looked yet" for the current scope.
     *
     * ⚠️ THE BUTTON NOW FOLLOWS THE EVIDENCE, RATHER THAN THE EVIDENCE FOLLOWING THE BUTTON.
     * Previously the control was rendered for every real batch day and the filer only discovered on
     * CLICK whether anything had been retained. On staging and UAT nothing ever has been -- automatic
     * filing is not enabled there, so no submission evidence is written -- which meant the control was
     * permanently present and permanently failed. Kelly: "can the error be more clear OR can we hide
     * the button unless a filing exists? Otherwise this is going to confuse the MISC team."
     *
     * So the lookup moved off the click and onto the selection, and the button is rendered only when a
     * download genuinely exists. The filer is never offered an action that cannot succeed.
     *
     * ⚠️ THIS IS NOT A RETREAT FROM #477's ERROR DISTINCTION. That fix exists because the screen must
     * never assert "nothing was retained" on the strength of a failure it never inspected. It still
     * never does: on a failed lookup this asserts NOTHING -- it shows no message and offers no button,
     * rather than claiming the record is empty. And when the button IS shown, a click acts on evidence
     * already in hand, so the integrity verdict is still shown before the bytes are handed over.
     */
    const [originalArtifacts, setOriginalArtifacts] = useState<any[] | null>(null);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    /**
     * IS A BATCH IN FLIGHT, OR STILL TO BE ISSUED?
     *
     * ⚠️ Starts TRUE. The screen is loading from the moment it mounts — the load effect has not run
     * yet, and once it does it may deliberately hold the request until a URL-promised Client / State
     * resolves (see the scope gate below). Starting false meant the grid rendered zero rows with no
     * loading state for that whole window and flashed "No rows" at a filer whose batch was on its way;
     * an empty queue is a meaningful claim on this screen and must not be made before it is known.
     * The one path that never issues a request — no token — lowers it explicitly.
     */
    const [loading, setLoading] = useState(true);

    // ONE PLACE THAT DECIDES WHAT A FILTER CHANGE DOES TO THE OTHER FILTERS.
    //
    // The page keeps a useState per filter because the rest of the component reads them individually,
    // but every TRANSITION between filter states goes through ../helper/batchReviewFilters, so the
    // rules are testable without standing up this component. Read the current values into a plain
    // object, hand it to a transition, write the result back.
    //
    // Deliberately NOT a useMemo. Its only consumers are three inline event handlers and one boolean,
    // so memoising saves nothing measurable -- and a dependency array listing every filter is a
    // standing trap: add a seventh filter, forget the dep, and the transitions silently read a stale
    // value. Building the object each render has no such failure mode.
    const currentFilterValues: BatchReviewFilterValues<PartnerOption, StateOption> = {
        partner: selectedPartner,
        state: selectedState,
        batchDate: batchDateFilter,
        insuredName: insuredNameFilter,
        policyNumber: policyNumberFilter,
        filingStatus: filingStatusFilter,
    };

    // ONE RELOAD, NOT SIX. These setters run inside a React event handler, so React 18 batches them
    // into a single commit. The load effect (deps: token, selectedPartner, selectedState, reloadNonce,
    // batchDateFilter) therefore fires ONCE no matter how many filters a transition touches, which is
    // what keeps Clear Filters from firing a cascade of requests it would then have to discard.
    const applyFilterValues = useCallback((next: BatchReviewFilterValues<PartnerOption, StateOption>) => {
        setSelectedPartner(next.partner);
        setSelectedState(next.state);
        setBatchDateFilter(next.batchDate);
        setInsuredNameFilter(next.insuredName);
        setPolicyNumberFilter(next.policyNumber);
        setFilingStatusFilter(next.filingStatus);
        // A change of filter scope drops the ticked rows in the SAME commit, so there is no frame in
        // which the header shows one filter while rows armed for filing belong to another. The load
        // effect clears them again when it starts; doing it here as well is what closes that frame.
        setSelectedRows([]);
        // The notice describes one specific batch day, so a scope change makes it stale.
        setBatchDownloadNotice(null);
    }, []);

    const handleClearFilters = useCallback(() => {
        applyFilterValues(clearedFilterValues<PartnerOption, StateOption>());
    }, [applyFilterValues]);

    const filtersAreActive = isAnyFilterActive(currentFilterValues);

    // The days that actually have a batch, scoped to whatever Client / State is selected. Reloaded on
    // scope change so the picker never offers a day that belongs to a client the filer is not looking at.
    useEffect(() => {
        // Same gate, and for the same reason, as the batch fetch below: an arrival carrying a client /
        // state / policy in the URL would otherwise ask for the unscoped day list first and throw the
        // answer away when the scoped one arrived a moment later.
        if (!token || !scopeSettled) return;
        // The days on offer belong to the scope that was asked for, so a scope change makes them wrong
        // rather than merely out of date — leaving them up offers a filer a day that belongs to a
        // client they are no longer looking at. Empty until the new answer lands, same as the Download
        // Original lookup. (Every transition that can reach here also clears the selected day; see
        // ../helper/batchReviewFilters.)
        setBatchDateOptions([]);
        const query = new URLSearchParams({
            partnersId: String(scopePartnersId),
            stateId: String(scopeStateId),
        });
        // Cancel a superseded lookup rather than letting it run to completion unread. The picker is a
        // secondary control, so this is mostly about not holding a connection open behind the batch
        // fetch that the filer is actually waiting on.
        const controller = new AbortController();
        let cancelled = false;
        apiClient(`/AutomaticFiling/filingBatchDates?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        }).then((res: any) => {
            if (cancelled) return;
            const days = res?.data?.batchDates;
            setBatchDateOptions(Array.isArray(days) ? days : []);
        }).catch(() => {
            // A picker that cannot load must not take the screen with it: the queue still works, it
            // simply offers no day to filter by. Failing loudly here would block a filer from the batch
            // review over a secondary control.
            if (cancelled) return;
            setBatchDateOptions([]);
        });
        return () => { cancelled = true; controller.abort(); };
    }, [token, scopeSettled, scopePartnersId, scopeStateId]);

    useEffect(() => {
        if (!token) return;
        apiClient('/Partners/getPartners', {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res: any) => {
            if (Array.isArray(res?.data)) setPartners(res.data);
        }).finally(() => {
            // Settled, not succeeded — see `partnersSettled`. A client list that never arrives must
            // still let the batch load, or a transient failure here becomes a permanently blank screen.
            setPartnersSettled(true);
        });
    }, [token]);

    useEffect(() => {
        if (!token) return;
        // Only states cut over to the automated filing pipeline (StateFilingCapability registry) —
        // batch review depends on the same per-policy review-building this flag gates, so a state that
        // isn't filing-enabled would never have a usable batch here anyway.
        apiClient('/AutomaticFiling/getFilingCapabilities', {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res: any) => {
            const capabilities = Array.isArray(res?.data) ? res.data : [];
            const cutoverStates = capabilities
                .filter((c: any) => c.filingEnabled && c.stateId != null)
                // Label with the SPACED displayName ("South Dakota"), not the space-free route segment
                // `name` ("SouthDakota") which is only for the /AutomaticFiling/<segment> URL. stateSegment()
                // still resolves the route from the spaced name via its aliases. Falls back to name for
                // any older API response without displayName.
                .map((c: any) => ({ stateId: c.stateId, stateName: c.displayName ?? c.name, postalAbbv: c.postalAbbv }));
            setStates(cutoverStates);
        }).finally(() => {
            setStatesSettled(true);
        });
    }, [token]);

    // FETCH the deep-linked policy as soon as there is a token — in PARALLEL with the two option lists,
    // not behind them. The lookup itself needs nothing but the id; the cutover-states list is only
    // needed to MATCH the resolved StateId to a {stateId, stateName} option, which the effect below
    // does once both have landed. Chaining it behind the states request put a whole extra round trip in
    // front of the ~3s batch query on the one path this screen is opened from most often ("View
    // Extraction Details").
    useEffect(() => {
        if (!token || !deepLinkCustomerPolicyId || deepLinkPrefillAttempted) return;
        setDeepLinkPrefillAttempted(true);
        const query = new URLSearchParams({ customerPolicyId: deepLinkCustomerPolicyId });
        let cancelled = false;
        apiClient(`/AutomaticFiling/getExtractionPolicyByCustomerPolicyId?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res: any) => {
            // `null` — as opposed to `undefined` — is "the lookup finished and there is no such policy".
            // The apply effect below waits on `undefined` and must be able to tell the two apart.
            if (!cancelled) setDeepLinkPolicy(res?.data ?? null);
        }).catch(() => {
            if (!cancelled) setDeepLinkPolicy(null);
        });
        return () => { cancelled = true; };
    }, [token, deepLinkCustomerPolicyId, deepLinkPrefillAttempted]);

    // Apply it once the state list is available to match against. Silently no-ops if the policy isn't
    // found or its state isn't in the cutover list — the filer just lands on the plain empty-selection
    // screen in that case, same as navigating here directly.
    useEffect(() => {
        if (!deepLinkCustomerPolicyId || policyScopeResolved) return;
        // Still waiting on one half or the other. Nothing may be applied, and the gate stays shut.
        if (deepLinkPolicy === undefined || !statesSettled) return;
        if (deepLinkPolicy) {
            const row = deepLinkPolicy;
            if (row.partnersId != null && row.client) {
                setSelectedPartner({ partnersId: row.partnersId, customerName: row.client });
            }
            const matchedState = states.find((s) => s.stateId === row.stateId);
            if (matchedState) setSelectedState(matchedState);
            if (row.extractedDocumentDataId != null) setDeepLinkTargetRowId(row.extractedDocumentDataId);
        }
        // Releases the scope gate, in the SAME commit as the selection it applies — so the batch fetch
        // sees the scoped ids and the open gate together, and fires once rather than twice. Set whether
        // the policy was found, not found, or the lookup failed: the answer to "is a narrower scope
        // still coming?" is now no either way, and a filer whose deep link could not be resolved still
        // gets the default batch instead of a screen that loads forever.
        setPolicyScopeResolved(true);
    }, [deepLinkCustomerPolicyId, policyScopeResolved, deepLinkPolicy, statesSettled, states]);

    /** Monotonic id for the batch fetch; only the newest response may write state. See the guard below. */
    const loadSeqRef = useRef(0);

    // Preselect the client/state from ?partnersId/?stateId ONCE, when the option lists first load (the
    // back-from-Client-Defaults round-trip). Set directly rather than through applyFilterValues: this is
    // rehydration, not a filter change, so it must not clear the Batch Date the URL is restoring the
    // filer to. (It also used to have to bypass the Client onChange because that cleared the State --
    // it no longer does, but rehydration still belongs outside the transitions.)
    //
    // ONE-SHOT (bugfix): these must NOT re-fire when the user clears a filter. Previously the guard was
    // just `selectedPartner`/`selectedState` being falsy, so clicking the Autocomplete's X set the value
    // to null → this effect saw null + the still-present URL param → immediately re-selected it, and the
    // clear appeared to "not work". The `*ScopeResolved` flag each effect sets is also its one-shot
    // guard, so the preselect runs exactly once (as soon as its option list has settled), after which
    // clearing sticks. The URL-sync effect below then drops the param.
    //
    // ⚠️ Keyed on the list having SETTLED, not on it having rows, and the resolved flag is raised in the
    // same commit as the selection it applies. Both matter: keying on `length === 0` meant an empty or
    // failed client list left the preselect pending forever, and raising the flag in a later commit
    // would let the batch fetch fire once for the selection and once for the gate opening.
    useEffect(() => {
        if (partnerScopeResolved || !partnersSettled) return;
        if (deepLinkPartnersId && !selectedPartner) {
            const p = partners.find((x) => String(x.partnersId) === deepLinkPartnersId);
            if (p) setSelectedPartner(p);
        }
        setPartnerScopeResolved(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkPartnersId, partnersSettled, partnerScopeResolved]);

    useEffect(() => {
        if (stateScopeResolved || !statesSettled) return;
        if (deepLinkStateId && !selectedState) {
            const s = states.find((x) => String(x.stateId) === deepLinkStateId);
            if (s) setSelectedState(s);
        }
        setStateScopeResolved(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkStateId, statesSettled, stateScopeResolved]);

    useEffect(() => {
        if (!token) {
            setPolicies([]);
            setPoliciesLoaded(false);
            setSelectedRows([]);
            // Nothing will be requested, so the row area must stop claiming to be loading.
            setLoading(false);
            return;
        }

        // DON'T ASK THE EXPENSIVE QUESTION UNTIL WE KNOW WHICH QUESTION TO ASK.
        //
        // This request is ~3 seconds on the server, almost all of it database round trips, and the
        // unscoped `partnersId=0&stateId=0` form is the most expensive shape of it. An arrival carrying
        // ?partnersId / ?stateId / ?CustomerPolicyId is going to narrow that scope within a few hundred
        // milliseconds, so issuing the broad one first buys nothing and is thrown away by the guard
        // below AFTER the server has done all the work. See ../helper/batchReviewLoadScope.
        //
        // When the URL promises nothing, this is true on the first render and the default all-clients
        // load fires immediately, exactly as before (Jerry, 2026-07-16).
        //
        // The row area stays busy while we wait. `loading` starts true, but the no-token branch above
        // lowers it — so a token that arrives late (auth still resolving on a deep link) would
        // otherwise leave the grid claiming an empty queue for the whole preselect round trip.
        if (!scopeSettled) {
            setLoading(true);
            return;
        }

        // STALE-RESPONSE GUARD. A deep link fires this effect three times (mount, partner, state), so
        // three fetches are in flight at once and nothing guarantees they resolve in order. Without this
        // an EARLIER, broader response landing last would overwrite `policies` with the wrong result set
        // — and, now that the callback prunes expansions against its own `loaded`, would also collapse
        // the deep-linked panel it does not contain. Only the newest request is allowed to write.
        const seq = ++loadSeqRef.current;
        setLoading(true);
        setPoliciesLoaded(false);
        // Drop the selection the moment a reload STARTS, not when it lands. Clearing it only in the
        // .then() left a window where the header already showed the new Client/State filter while
        // `policies` still held the previous filter's rows — so a click on File during the in-flight
        // request submitted rows belonging to a client the filer was no longer looking at. The File
        // button is additionally disabled while `loading` for the same reason.
        setSelectedRows([]);
        // Default view is ALL clients + ALL states (partnersId/stateId = 0 on the backend); the Client
        // and State pickers just NARROW it. So the batch loads every extracted-and-sent policy up front
        // instead of forcing a client+state selection first (Jerry, 2026-07-16).
        const query = new URLSearchParams({
            partnersId: String(scopePartnersId),
            stateId: String(scopeStateId),
            pageNumber: '1',
            pageSize: String(BATCH_FETCH_SIZE),
            // Ask for the GRID's data only. The per-field review detail is fetched per row on expand
            // (loadRowFields below); it was 83.6% of this response and the collapsed table renders none
            // of it. An internal-api that predates the flag simply ignores it and still inlines the
            // list, which the row mapping below detects and treats as already-loaded.
            includeFields: 'false',
        });
        // Only a REAL day is sent to the server. NO_BATCH_DATE ("no recorded batch date") is a
        // client-side predicate over the loaded rows: it is the complement of a date range rather than
        // a date, and the SQL filter is a window comparison that a policy with NO transition at all can
        // never satisfy.
        if (batchDateFilter && batchDateFilter !== NO_BATCH_DATE) {
            query.set('batchDate', batchDateFilter);
        }

        // ⚠️ ABORT, DON'T JUST IGNORE. `loadSeqRef` has always stopped a superseded response from
        // WRITING, but the request itself still ran to completion — so changing Client, then State,
        // then Batch Date left three ~3-second whole-book queries executing on the server, competing
        // for the same connection pool as the one the filer is actually waiting for, and holding one of
        // the browser's few connections to that origin. Cancelling costs nothing and is the difference
        // between the last filter change being served immediately and being served behind its
        // predecessors. The sequence guard stays and remains the ONE mechanism that decides who may
        // write: it is what keeps a response that has already been received from writing, which an
        // abort can no longer prevent. Unmounting bumps it too (see the cleanup), so there is no second
        // "superseded" flag to keep in step with it.
        const controller = new AbortController();
        // A request for exactly this scope may already be in flight: `primeBatchReviewLoad` starts the
        // unscoped default at startup, in parallel with the auth and feature-flag round-trips that
        // would otherwise have to finish before this screen could even mount. Adopt it when the key and
        // the bearer both match; it is handed over at most once, so every later reload — a filter
        // change, a manual refresh — issues a real request through the abortable path below.
        //
        // ⚠️ AN ADOPTED REQUEST CARRIES NO SIGNAL, SO IT CANNOT BE CANCELLED — and that is acceptable
        // because cancellation was never what protected correctness here. `loadSeqRef` is the write
        // gate: every writer below checks it, and the cleanup bumps it BEFORE aborting, so an adopted
        // response that resolves after a newer request or after unmount still cannot write. Aborting is
        // an efficiency measure (stop a superseded ~3s whole-book query competing for the pool), and
        // the one request that cannot be aborted is the one already in flight before this screen
        // existed — at most one per page load, never one of the repeated filter-change queries that
        // made cancellation worth adding. `controller` is still created and still aborted by the
        // cleanup; on the adopted path that is a no-op on an unused controller.
        const adopted = takeBatchReviewLoad(query.toString(), token);
        (adopted ?? apiClient(`/AutomaticFiling/BatchReview?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        })).then((res: any) => {
            // Superseded by a newer load — drop it on the floor rather than letting it write anything.
            if (seq !== loadSeqRef.current) return;
            const rows = res?.data?.data;
            const loaded: BatchReviewRow[] = Array.isArray(rows) ? rows.map((r: any) => ({
                ...r,
                fieldExceptions: r.fieldExceptions || [],
                gaps: r.gaps || [],
                blockers: r.blockers || [],
                blockerFieldKeys: r.blockerFieldKeys || [],
                warnings: r.warnings || [],
                manualReview: r.manualReview || [],
                declinations: r.declinations || [],
                fields: r.fields || [],
                // An older internal-api still inlines `fields`; a current one omits the key entirely
                // when includeFields=false. Presence — not emptiness — is what distinguishes "already
                // have it" from "not fetched yet", so both API builds work unchanged.
                fieldsLoaded: Array.isArray(r.fields),
            })) : [];
            setPolicies(loaded);
            // A reload replaces every row and resets them to unloaded, so previous detail failures are
            // no longer about anything on screen — clearing them lets a reopened row try again.
            setFailedFieldRowIds([]);
            // Server-side match count, so the grid can say when it is showing a subset rather than
            // quietly presenting the first page as the whole batch.
            const total = Number(res?.data?.totalRecords);
            setTotalAvailable(Number.isFinite(total) && total >= 0 ? total : null);
            setSelectedRows([]);
            setPoliciesLoaded(true);
            // Latched on the first batch that lands and never lowered again, so a later reload cannot
            // unmount the filter bar underneath the control the user is currently operating.
            setHasLoadedOnce(true);
            // Back to page 1 on every (re)load: a reload after bulk-filing can shrink the set below the
            // current page's offset, which would otherwise render an empty grid. The deep-link effect
            // re-pages afterward if it has a target.
            setPage(0);

            // THE BUG BEHIND "it opens and then immediately closes again" (Josh 2026-07-31; still
            // reproducing after the first attempt at a fix, which addressed the wrong mechanism).
            //
            // This used to be an if/else that OPENED the deep-linked row here and, in its else branch,
            // ran an unconditional `setDetailPanelExpandedRowIds([])`. The problem is that this effect
            // runs SEVERAL times while a deep link is arriving: its deps are
            // [token, selectedPartner, selectedState, reloadNonce], and a URL carrying both ?partnersId
            // and ?stateId sets two of those from separate one-shot preselect effects. So arriving at
            //   /FilingQueue/BatchReview?CustomerPolicyId=…&partnersId=…&stateId=…
            // fires it on mount, again when the partner lands, and again when the state lands — three
            // overlapping fetches.
            //
            // `deepLinkTargetRowId` is consumed by the FIRST response that finds its row (nulled so the
            // jump is one-shot). Every LATER response then found no target, fell into the else, and
            // wiped the expansion the deep link had just opened. A direct state write from a
            // stale-but-legitimate response — nothing to do with the grid, which is why the earlier
            // pin (it only filters changes REPORTED BY the grid) could never have fixed it.
            //
            // Two changes, so the whole class goes away rather than this one instance:
            //   1. OPENING moved out of this callback into its own effect below, keyed on the target AND
            //      the loaded rows. Response ordering can no longer decide whether the panel opens —
            //      whenever both are present, it opens. That also fixes the mirror-image race where the
            //      target arrived after the last response and the panel never opened at all.
            //   2. This callback now only PRUNES: drop expansions whose row is genuinely gone from the
            //      new result set. That is the honest meaning of the branch — "forget rows that no
            //      longer exist", not "collapse whatever the user is looking at". It also stops the same
            //      collapse after any other reload (re-extract, save, bulk file) that leaves the row in
            //      place.
            setDetailPanelExpandedRowIds((prev) => prev.filter(
                (id) => loaded.some((r) => r.extractedDocumentDataId === id),
            ));
        }).catch(() => {
            // ⚠️ THERE WAS NO CATCH HERE. `setPoliciesLoaded(true)` only ran in the success path, and the
            // filter bar was gated on that flag — so a rejected load (401, 500, dropped connection) left
            // Insured Name, Policy Number and Batch Date unmounted with nothing able to bring them back
            // except another load the user could no longer start, because the controls that start one
            // were the ones that had vanished. The latch above now keeps them mounted regardless; this
            // makes the failure visible instead of silent, and leaves the page recoverable.
            if (seq !== loadSeqRef.current) return;
            // ⚠️ ROWS FROM A SCOPE THAT IS NO LONGER ON SCREEN ARE NOT KEPT. The header already shows
            // the Client / State the filer just chose; leaving the previous scope's rows under it would
            // put policies belonging to one client inside a view labelled another, with the File button
            // re-enabled the moment `loading` drops. That is the same hazard the mid-flight selection
            // clear exists to prevent, and a failed load is exactly when it would bite. `policiesLoaded`
            // stays false, so this reads as "unknown", not as "this batch is empty".
            setPolicies([]);
            setTotalAvailable(null);
            setSelectedRows([]);
            // ⚠️ THE LATCH IS RAISED ON FAILURE TOO, AND THAT IS THE POINT. Setting it only on success
            // would leave the FIRST-load failure in exactly the state this fix exists to prevent: no
            // batch has landed, so the filter bar has never been shown, and the controls needed to
            // retry are the ones still hidden. Recovery has to be reachable from a failed first load.
            setHasLoadedOnce(true);
            setBatchDownloadNotice({
                severity: 'error',
                message: 'Could not load the batch. The filters are still available — adjust them or try again.',
            });
        }).finally(() => { if (seq === loadSeqRef.current) setLoading(false); });
        // Bumping the sequence here is what makes UNMOUNT a supersede too, so the callbacks above need
        // no separate flag: after this, `seq === loadSeqRef.current` is false for every one of them.
        return () => { loadSeqRef.current += 1; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, scopeSettled, scopePartnersId, scopeStateId, reloadNonce, batchDateFilter]);

    // Deep-link: open the specific policy's row once BOTH the target and the rows containing it exist.
    //
    // Deliberately an effect over state rather than a step inside the fetch callback above. The target
    // (resolved by its own async lookup) and the rows (resolved by a batch fetch that runs up to three
    // times on arrival) settle in a NON-DETERMINISTIC order, and doing this in whichever callback
    // happened to finish last is what made the panel open-then-close. Keyed on both, it simply waits
    // until they agree — no ordering assumption at all.
    //
    // Still one-shot: the target is cleared as soon as it is used, so re-picking the same client/state,
    // or any later reload, cannot yank the filer back to this row.
    useEffect(() => {
        if (deepLinkTargetRowId == null || !policiesLoaded) return;
        const target = policies.find((r) => r.extractedDocumentDataId === deepLinkTargetRowId);
        // Not in this result set (yet, or at all) — leave the target armed. A narrowing reload that is
        // still in flight may bring it; if it never does, nothing was promised.
        if (!target) return;
        setDeepLinkTargetRowId(null);
        // The default "Ready to file" tab would hide a target row in any other lifecycle state (not
        // ready, already filed, needs attention) — widen to All so it is actually visible rather than
        // silently filtered out.
        if (!isRowFilable(target)) setFilingStatusFilter('all');
        setDetailPanelExpandedRowIds([target.extractedDocumentDataId]);
        // Hold the panel open through the re-filter/re-page that follows; released once the row has
        // landed on its page (see the scroll effect below).
        deepLinkPinnedRowIdRef.current = target.extractedDocumentDataId;
        // The grid pages at 50, so the target may not be on the current page — its DOM node would not
        // exist and the deep link would silently land on page 1. Hand off to the scroll effect, which
        // moves to the row's page FIRST and only then scrolls.
        setDeepLinkScrollRowId(target.extractedDocumentDataId);
    }, [deepLinkTargetRowId, policies, policiesLoaded]);

    // Keep the selected client + state in the URL so navigating away (e.g. clicking into a document /
    // the customer page) and pressing Back restores the batch the filer was in — the deep-link effects
    // above already rehydrate selectedPartner/selectedState from partnersId/stateId, so this is the
    // write half. replace:true so it doesn't spam browser history on every pick.
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        const pid = selectedPartner?.partnersId ? String(selectedPartner.partnersId) : '';
        const sid = selectedState?.stateId ? String(selectedState.stateId) : '';
        if ((params.get('partnersId') || '') === pid && (params.get('stateId') || '') === sid) return;
        if (pid) params.set('partnersId', pid); else params.delete('partnersId');
        if (sid) params.set('stateId', sid); else params.delete('stateId');
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartner, selectedState]);

    // Changing a filter narrows what's visible; return to page 1 -- a narrowed result set can be
    // shorter than the current page offset, which would render an empty grid. The selection is
    // reconciled by the effect below rather than here, so there is ONE mechanism keeping it honest.
    useEffect(() => {
        setPage(0);
    }, [filingStatusFilter, insuredNameFilter, policyNumberFilter, batchDateFilter]);

    // Paging away from a selection drops it. DataGridPro deliberately keeps checkbox selection across
    // pages, which is right for a reversible bulk edit and wrong for an irreversible filing: rows on a
    // page the filer has left are invisible but were still going into the payload. For this action the
    // rule is "what gets filed is exactly what is on screen and ticked", so leaving the page clears it.
    useEffect(() => {
        setSelectedRows([]);
    }, [page, pageSize]);

    /**
     * Is the loaded batch a SUBSET of what actually matched?
     *
     * The server's count is preferred but not required: if totalRecords is missing or unparseable we fall
     * back to "we loaded exactly the cap", which is itself strong evidence of truncation. Otherwise a
     * metadata failure would silence the warning at exactly the moment the set is most likely incomplete.
     */
    const isTruncated = totalAvailable != null
        ? policies.length < totalAvailable
        : policies.length >= BATCH_FETCH_SIZE;
    const loadedCount = policies.length.toLocaleString();

    const visiblePolicies = useMemo(() => {
        const nameQ = insuredNameFilter.trim().toLowerCase();
        const polQ = policyNumberFilter.trim().toLowerCase();
        return policies.filter((p) => {
            if (!matchesLifecycleTab(p, filingStatusFilter)) return false;
            // ⚠️ ONLY the sentinel is handled here. A real day is applied SERVER-side, so re-checking it
            // would put the same rule in two places; "no batch date recorded" is the complement of a
            // date range rather than a date, which a window comparison cannot express for a policy that
            // has no transition at all, so it is the one case that has to live on the client.
            if (batchDateFilter === NO_BATCH_DATE && batchDateOf(p) !== null) return false;
            if (nameQ && !(p.insuredName || '').toLowerCase().includes(nameQ)) return false;
            if (polQ && !(p.policyNumber || '').toLowerCase().includes(polQ)) return false;
            return true;
        });
    }, [policies, filingStatusFilter, insuredNameFilter, policyNumberFilter, batchDateFilter]);

    /** Per-tab row counts for the lifecycle strip. Memoised on `policies` because the strip now renders
     *  on every pass — including every keystroke in the Insured Name / Policy Number boxes — and this is
     *  six passes over up to BATCH_FETCH_SIZE rows. It only changes when the batch does. */
    const lifecycleCounts = useMemo(
        () => LIFECYCLE_TABS.map((t) => ({ ...t, count: policies.filter((p) => matchesLifecycleTab(p, t.value)).length })),
        [policies],
    );

    /**
     * DOES AN ORIGINAL ACTUALLY EXIST FOR THE SELECTED BATCH DAY?
     *
     * Asked on SELECTION, so the Download button below can be rendered only when the answer is yes.
     * The filer is never offered an action that cannot succeed, which is the whole point: on staging
     * and UAT nothing is ever retained, because automatic filing is not enabled there and submission
     * evidence is only written by a real submission. Offering the button anyway produced a permanent,
     * unavoidable error message on a screen the MISC team is meant to trust.
     *
     * ⚠️ A FAILED LOOKUP ASSERTS NOTHING. It leaves the button hidden and shows no message. That is
     * deliberately NOT the defect #477 fixed: that defect was the screen SAYING "no original was
     * recorded" on the strength of an error it had not inspected. Saying nothing is not the same as
     * saying "nothing exists" — and the honest branches in `handleDownloadOriginalBatch` are still
     * there for the case where the button was shown and the click then fails.
     *
     * Deliberately scoped to Client + State as well as the day, matching what the download itself
     * sends, so the answer describes the same batch the button would fetch.
     */
    useEffect(() => {
        if (!token || !batchDateFilter || batchDateFilter === NO_BATCH_DATE) {
            setOriginalArtifacts(null);
            return;
        }
        // ⚠️ BACK TO "NOT LOOKED YET" BEFORE LOOKING AGAIN. Without this the answer for the PREVIOUS
        // scope stayed in place for the whole of the new lookup, so switching from a day that HAS a
        // retained original to one that does not left the Download button on screen — and a click in
        // that window refetched against the CURRENT scope and produced exactly the "not available"
        // failure this control exists to prevent. `null` means unknown, and unknown hides the button,
        // which is the conservative reading and the one the "no flicker" note below assumes.
        setOriginalArtifacts(null);
        let cancelled = false;
        const query = new URLSearchParams({
            batchDate: batchDateFilter,
            partnersId: String(scopePartnersId),
            stateId: String(scopeStateId),
        });
        // Superseded lookups are cancelled, not merely ignored — the same reason as the batch fetch.
        const controller = new AbortController();
        apiClient(`/AutomaticFiling/filingBatchArtifact?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        }).then((res: any) => {
            if (cancelled) return;
            // Only a well-formed success may be read as "an original exists". Anything else -- an
            // unreachable server, a non-2xx, a non-JSON body -- leaves the button hidden and says
            // nothing about the record either way.
            const artifacts = (res && res.ok !== false && Array.isArray(res?.data?.artifacts))
                ? res.data.artifacts
                : [];
            setOriginalArtifacts(artifacts);
        }).catch(() => {
            if (cancelled) return;
            setOriginalArtifacts([]);
        });
        return () => { cancelled = true; controller.abort(); };
    }, [token, batchDateFilter, scopePartnersId, scopeStateId]);

    /**
     * Download the ORIGINAL artifact(s) for the selected batch day.
     *
     * ⚠️ RETRIEVED, NOT REBUILT — and that is the whole difference between this and the existing
     * "Download all". "Download all" BUILDS a batch file from today's policy data, which is right for a
     * batch you are about to send and wrong for one already sent: it would fold in every correction
     * made since and hand over a document that quietly differs from the one the state holds. This asks
     * the API for the bytes that were actually transmitted, and reports whether they still verify
     * against the SHA-256 captured at transmit time.
     */
    const handleDownloadOriginalBatch = useCallback(async () => {
        if (!token || !batchDateFilter || batchDateFilter === NO_BATCH_DATE) return;
        setDownloadingBatch(true);
        try {
            const query = new URLSearchParams({
                batchDate: batchDateFilter,
                partnersId: String(selectedPartner?.partnersId ?? 0),
                stateId: String(selectedState?.stateId ?? 0),
            });
            const res: any = await apiClient(`/AutomaticFiling/filingBatchArtifact?${query.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // ⚠️ A SERVER FAILURE IS NOT THE SAME FACT AS "NO EVIDENCE EXISTS", AND THIS SAID IT WAS.
            // `apiClient` never throws: on any non-ok response it returns `{ ok: false, status, ... }`,
            // which has no `.data`. So a 500 and a genuine 404 both fell into the branch below and the
            // filer was told "no original submitted file was recorded" — an audit surface asserting that
            // nothing was retained, on the strength of an error it never looked at. For most of this
            // feature's life that is exactly what happened: the artifacts query was invalid T-SQL and
            // every call was a 500. Only a 404 may speak about the historical record.
            // Read top-down: could not reach it → reached it and it failed → reached it and it worked.
            if (!res) {
                setBatchDownloadNotice({
                    severity: 'error',
                    message: 'Could not reach the server to retrieve the original submitted file. Try again.',
                });
                return;
            }
            // `typeof res.status === 'number'` keeps this to genuine HTTP failures. `apiClient`
            // camel-cases a successful JSON body straight through, so an application envelope that
            // happens to carry `ok: false` would otherwise be reported as "HTTP undefined".
            if (res.ok === false && typeof res.status === 'number') {
                if (res.status !== 404) {
                    setBatchDownloadNotice({
                        severity: 'error',
                        message: `Could not retrieve the original submitted file for ${batchDateFilter} (HTTP ${res.status}). `
                            + 'This is a retrieval failure, not a statement about whether the file exists.',
                    });
                    return;
                }
                setBatchDownloadNotice({
                    severity: 'warning',
                    message: `Original submitted file is not available for ${batchDateFilter}.`,
                });
                return;
            }
            // ⚠️ A MALFORMED 200 IS STILL A FAILURE, NOT AN ANSWER ABOUT THE RECORD. `apiClient` hands
            // back the raw Response for a non-JSON success, so `res.data` is undefined and the missing
            // `artifacts` array looks identical to "nothing was retained". Only a well-formed response
            // may be read as a statement about what history holds.
            if (!res.data || !Array.isArray(res.data.artifacts)) {
                setBatchDownloadNotice({
                    severity: 'error',
                    message: `Could not read the response for ${batchDateFilter}. `
                        + 'This is a retrieval failure, not a statement about whether the file exists.',
                });
                return;
            }
            const artifacts = res.data.artifacts;
            if (artifacts.length === 0) {
                // A well-formed 200 carrying no artifacts is the honest "nothing was retained for that
                // day" — expected for any batch whose cycle closed before evidence capture began.
                setBatchDownloadNotice({
                    severity: 'warning',
                    message: `Original submitted file is not available for ${batchDateFilter}. `
                        + 'No submission artifact was retained for that batch.',
                });
                return;
            }
            // ⚠️ The verdict is shown BEFORE the file is handed over, and a failure is not silent. An
            // artifact that does not match its transmit-time hash is exactly the case where an
            // unannotated download is worse than no download: it looks authoritative and is not.
            const integrity = res?.data?.integrity;
            setBatchDownloadNotice(integrityNotice(integrity, artifacts.length, batchDateFilter));

            const payload = artifacts
                .map((a: any) => `<!-- ${a.requestFileName || `submission ${a.submissionNumber || 'unknown'}`}`
                    + ` | ${a.policyCount ?? '?'} policies | sent ${a.submittedAt || 'unknown'}`
                    + ` | sha256 ${a.requestSha256 || 'not anchored'} | ${a.integrity} -->\n${a.requestBody ?? ''}`)
                .join('\n\n');
            const blob = new Blob([payload], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `original-submitted-batch-${batchDateFilter}.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error: any) {
            setBatchDownloadNotice({
                severity: 'error',
                message: `Could not retrieve the original batch: ${error?.message || 'request failed'}`,
            });
        } finally {
            setDownloadingBatch(false);
        }
    }, [token, batchDateFilter, selectedPartner, selectedState]);

    // ---- The one set that is both shown and filed -------------------------------------------------
    // Everything about the filing action derives from `selectedVisibleRows`: the button's count, the
    // confirm dialog's plan, and the request payload. Deriving them separately is what let the screen
    // say "File 1 policy" and file three. A row only qualifies if it is (a) still in the current
    // filtered view and (b) still filable — a row that has been filtered out, or that a reload has
    // moved to queued/filed, can no longer be submitted no matter what the checkbox state says.
    // SELECTABLE ≠ FILABLE. A row is selectable when it can be either filed OR inspected (a Manually
    // Filed row can be checked so a proof batch can be built for it), which is what makes select-all
    // work on the Manually Filed tab. `selectedVisibleRows` below stays on `isRowFilable`, so widening
    // the checkbox does NOT widen what can be submitted — see isRowInspectable's note.
    const selectableIds = useMemo(
        () => new Set(visiblePolicies.filter(isRowInspectable).map((p) => p.extractedDocumentDataId)),
        [visiblePolicies],
    );

    // Reconcile: prune any checked id that is no longer selectable. Covers a filter change, a text
    // search, and a reload that changed a row's lifecycle status, with one rule instead of three
    // scattered clears. Returns `prev` untouched when nothing changed so React bails out of the render.
    useEffect(() => {
        setSelectedRows((prev) => {
            const next = prev.filter((id) => selectableIds.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [selectableIds]);

    // THE FILING SET. Deliberately still `isRowFilable` and nothing wider — this is what the File
    // button counts and what the submit payload is built from.
    const selectedVisibleRows = useMemo(
        () => visiblePolicies.filter((p) => selectedRows.includes(p.extractedDocumentDataId) && isRowFilable(p)),
        [visiblePolicies, selectedRows],
    );

    // THE INSPECTION SET. Checked Manually Filed rows — never filable, only ever built into a proof
    // batch. Kept as its own memo rather than a filter at the call site so the button's count and the
    // request payload cannot drift apart, the same reason `selectedVisibleRows` exists.
    const selectedProofRows = useMemo(
        () => visiblePolicies.filter((p) => selectedRows.includes(p.extractedDocumentDataId) && isRowProofBatchEligible(p)),
        [visiblePolicies, selectedRows],
    );

    // Deep-link + paging: move to the page the target row is actually on, THEN scroll to it. Runs off
    // visiblePolicies (not the raw fetch) so the filter widening above has already been applied — the
    // row's index only means something once the list it indexes into is final. One-shot: clears the
    // target so a later re-render (filter change, page change) can't yank the filer back.
    useEffect(() => {
        if (deepLinkScrollRowId == null) return;
        const idx = visiblePolicies.findIndex((r) => r.extractedDocumentDataId === deepLinkScrollRowId);
        // Filtered out entirely — nothing to scroll to, and nothing to hold open either. Releasing the
        // pin here matters: leaving it set would make the panel permanently uncloseable.
        if (idx < 0) { setDeepLinkScrollRowId(null); deepLinkPinnedRowIdRef.current = null; return; }
        const targetPage = Math.floor(idx / pageSize);
        if (targetPage !== page) { setPage(targetPage); return; } // re-runs once the page has switched
        setDeepLinkScrollRowId(null);
        // SETTLED: targetPage === page, so the row set the grid is rendering is final and the target row
        // is mounted. Release the pin HERE, synchronously — not inside the timeout below.
        //
        // Two reasons it must not live in the timeout. (1) If the effect re-ran before the timeout fired
        // the cleanup would cancel it, the pin would never be released, and the panel would become
        // permanently uncloseable — a worse bug than the one being fixed. (2) Releasing synchronously is
        // simply true: from this line on, an empty expansion change really is the user closing the panel.
        deepLinkPinnedRowIdRef.current = null;
        // NOTE: deliberately does NOT re-assert setDetailPanelExpandedRowIds. An earlier draft did, as
        // belt-and-braces, but the pin is set in the same statement that opens the panel, so there is no
        // window where it could have been lost — and re-asserting after a delay would re-OPEN a panel the
        // user had deliberately closed inside that window. Reopening a panel someone just closed is a
        // more annoying bug than the one it guards against.
        //
        // The grid renders with autoHeight (the page scrolls, not an internal grid viewport), so scroll
        // the actual row element into view rather than using the grid's virtualization-scroll API.
        // Deferred a tick so the just-changed page and the newly-expanded detail panel have rendered —
        // scrolling on the same tick would target a stale layout.
        const settledRowId = deepLinkScrollRowId;
        const scrollTimer = window.setTimeout(() => {
            document.querySelector(`[data-id="${settledRowId}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
        // Cancel on unmount / re-run so a second deep link cannot be yanked back to the first one's row.
        return () => window.clearTimeout(scrollTimer);
    }, [deepLinkScrollRowId, visiblePolicies, pageSize, page]);

    // Rows carrying a clearinghouse reason render TWO lines in the Filing Status cell (chip, then the
    // reason). The default row height only fits one, which is what made the Needs Attention rows collide
    // with their neighbours. Taller ONLY for those rows — applying it to all 31 would add dead space to
    // the ~29 that have nothing on the second line. `null` means "use the grid default".
    // The emptiness test MUST match FilingStatusCell's exactly (it renders on the TRIMMED value), or a
    // whitespace-only reason buys a 74px row with nothing on its second line — unexplained dead space.
    const getRowHeight = useCallback<NonNullable<DataGridProProps['getRowHeight']>>(
        ({ model }) => (String((model as BatchReviewRow).filingStatusReason ?? '').trim() ? 74 : null),
        [],
    );

    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<GridRowId[]>([]);
    // DEEP-LINK PIN (Josh, 2026-07-31 — "opens up the policy, but then it immediately closes again").
    //
    // Arriving from "View Extraction Details" expands the target row's panel, and then the screen
    // immediately re-filters (to `all`, so a non-filable target is not hidden) and re-pages to wherever
    // the row actually is. Across those transitions the target row is briefly NOT on the rendered page,
    // so DataGridPro unmounts it and reports an EMPTY expansion — which the handler below faithfully
    // wrote back, closing the panel the deep link had just opened.
    //
    // While a deep link is settling, an empty change is therefore treated as the grid talking about
    // rows it is no longer rendering, NOT as the user closing the panel. A ref, not state, so the
    // handler stays referentially stable and the grid does not re-render on every pin change.
    const deepLinkPinnedRowIdRef = useRef<number | null>(null);
    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds: GridRowId[]) => {
        if (newIds.length === 0 && deepLinkPinnedRowIdRef.current != null) return;
        setDetailPanelExpandedRowIds(newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds);
    }, []);
    // LAZY ROW DETAIL. The grid response carries no per-field review list, so the row being opened
    // fetches its own. Keyed by extractedDocumentDataId (the grid's row id) while the request is in
    // flight so a double-expand — or a re-render while the fetch is outstanding — cannot fire it twice.
    //
    // Deliberately NOT cleared on collapse: a filer opening the same row again should not re-pay for
    // it, and the values may by then hold their unsaved edits. A batch reload replaces the rows (and
    // resets them to unloaded), which is the only point the detail can have gone stale.
    const fieldsInFlightRef = useRef<Set<number>>(new Set());
    // Rows whose detail fetch failed. Kept separate from `fieldsLoaded` so the panel can distinguish
    // "still loading" from "could not load" — the second needs a retry affordance, the first does not.
    // `retryFieldsFetch` clears the row's error AND bumps the nonce, which re-runs the effect below
    // even though the expanded-row set has not changed.
    const [failedFieldRowIds, setFailedFieldRowIds] = useState<number[]>([]);
    const [fieldsRetryNonce, setFieldsRetryNonce] = useState(0);
    // Mirror of the above for the frozen getDetailPanelContent callback, which cannot close over state.
    const failedFieldRowIdsRef = useRef<number[]>([]);
    failedFieldRowIdsRef.current = failedFieldRowIds;
    const setFieldsError = useCallback((rowId: number) => {
        setFailedFieldRowIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
    }, []);
    const retryFieldsFetch = useCallback((rowId: number) => {
        setFailedFieldRowIds((prev) => prev.filter((id) => id !== rowId));
        setFieldsRetryNonce((n) => n + 1);
    }, []);
    useEffect(() => {
        const targetId = detailPanelExpandedRowIds[detailPanelExpandedRowIds.length - 1];
        if (targetId == null) return;
        const rowId = Number(targetId);
        const row = policies.find((p) => p.extractedDocumentDataId === rowId);
        if (!row || row.fieldsLoaded || fieldsInFlightRef.current.has(rowId)) return;
        if (!row.customerPolicyId || !row.state) return;

        fieldsInFlightRef.current.add(rowId);
        // The batch's load sequence AT REQUEST TIME. A reload (save, re-extract, filter change) replaces
        // every row and resets them to unloaded; without this check a detail request already in flight
        // could land afterwards and mark the FRESH row loaded with the values read before the reload —
        // i.e. an expand panel quietly disagreeing with the row's own readiness and totals.
        const seq = loadSeqRef.current;
        const query = new URLSearchParams({
            customerPolicyId: String(row.customerPolicyId),
            state: toStateCode(row.state),
        });
        apiClient(`/AutomaticFiling/BatchReviewFields?${query.toString()}`, {
            headers: { Authorization: `Bearer ${tokenRef.current}` },
        }).then((res: any) => {
            if (seq !== loadSeqRef.current) return;   // superseded by a reload — drop it on the floor
            const fetched = res?.data?.fields;
            if (!Array.isArray(fetched)) { setFieldsError(rowId); return; }
            setPolicies((prev) => prev.map((p) => (
                p.extractedDocumentDataId === rowId ? { ...p, fields: fetched, fieldsLoaded: true } : p
            )));
        }).catch(() => {
            // Without this the panel would spin forever: `fieldsLoaded` never flips, and clearing the
            // in-flight marker below does not itself re-render. Surface it so the filer can retry
            // instead of being left looking at a spinner that will never resolve.
            if (seq === loadSeqRef.current) setFieldsError(rowId);
        }).finally(() => {
            fieldsInFlightRef.current.delete(rowId);
        });
        // `policies` is intentionally not a dependency: it changes on every field edit, which would
        // re-run this effect constantly. The row is read at fire time and the guards above make a
        // repeat run a no-op anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailPanelExpandedRowIds, fieldsRetryNonce]);

    const getDetailPanelContent = useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(
        ({ row }) => (
            <AreasToFixPanel
                row={row as BatchReviewRow}
                // tokenRef.current, NOT `token`. This callback is frozen ([] deps) but its BODY runs on
                // every panel render, so reading through the stable ref yields the live token while the
                // captured `token` binding stays stuck on the first render — which is what made the
                // panel's Save (line ~2112, it uses this prop) 401 after a mid-session token refresh.
                token={tokenRef.current}
                onFieldChange={(key, value) => handleRowFieldChange((row as BatchReviewRow).extractedDocumentDataId, key, value)}
                onCoverageChange={(code) => handleRowCoverageChange((row as BatchReviewRow).extractedDocumentDataId, code)}
                onViewDocument={(page) => handleViewDocument(row as BatchReviewRow, page)}
                onDownloadBatch={() => handleDownloadBatch(row as BatchReviewRow)}
                onReExtract={() => handleReExtract(row as BatchReviewRow)}
                onSaved={() => setReloadNonce((n) => n + 1)}
                // Read through refs for the same reason `token` is: this callback is frozen ([] deps)
                // but its body runs on every panel render, so a captured value would go stale.
                fieldsFailed={failedFieldRowIdsRef.current.includes((row as BatchReviewRow).extractedDocumentDataId)}
                onRetryFields={() => retryFieldsFetch((row as BatchReviewRow).extractedDocumentDataId)}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    // MUI X v5 has no getDetailPanelHeight => 'auto', so we estimate the rendered height and keep the
    // slack small (~1 row) instead of over-reserving. The filing-details grid is auto-fill minmax(210px)
    // → ~4 columns on a desktop panel, so rows ≈ ceil(fields/4). Document rows are taller (they carry a
    // provenance line); DB/CALC rows are shorter. Under-reserving would clip fields, so a small buffer.
    const getDetailPanelHeight = useCallback((params: GridRowParams) => {
        const row = params.row as BatchReviewRow;
        // Detail still loading: reserve a small panel for the spinner rather than computing a height
        // from an empty field list, which would read as a real "this policy has no fields" layout. The
        // row object is replaced when the fetch lands, so the grid re-measures with the real estimate.
        if (!row.fieldsLoaded) return 140;
        const gapsCount = (row.gaps || []).length;
        const blockersCount = (row.blockers || []).length;
        const warningsCount = (row.warnings || []).length;
        const manualReviewCount = (row.manualReview || []).length;
        const lloydsFlagCount = (row.manualReview || []).filter((f) => f.kind === 'lloyds_syndicate_confirm').length;
        const requiredByGroup: Record<string, number> = {};
        for (const f of row.fields || []) {
            if (f.required === false) continue;
            requiredByGroup[f.group] = (requiredByGroup[f.group] || 0) + 1;
        }
        const COLS = 4;
        const rowHeightFor = (group: string) => (group === 'document' ? 82 : group === 'computed' ? 66 : 54);
        const groupCount = Object.keys(requiredByGroup).length;
        const fieldSectionsHeight = Object.entries(requiredByGroup).reduce(
            (sum, [group, count]) => sum + 24 /* group sub-header */ + Math.ceil(count / COLS) * rowHeightFor(group),
            0,
        );
        // detail-top + FILING DETAILS legend + any review bands + field sections + inter-group gaps + buffer.
        return 88
            + 30 /* filing-details legend */
            + (gapsCount > 0 ? 22 + gapsCount * 44 : 0)
            + (manualReviewCount > 0 ? 22 + manualReviewCount * 32 : 0)
            + (blockersCount > 0 ? 22 + blockersCount * 20 : 0)
            // A submitted row says more in two bands: the blockers band gains a sub-caption explaining
            // that nothing there is outstanding, and each Lloyd's flag's suffix grows ("…so the next one
            // can file automatically" vs "…before filing"), which can wrap an extra line per flag. The
            // panel card is overflow:hidden with no internal scroll, so under-reserving clips the last
            // field row rather than scrolling to it. See `blockerBandCopy` / `lloydsConfirmCopy`.
            + (isRowSubmitted(row)
                ? (blockersCount > 0 ? 40 : 0) + lloydsFlagCount * 17
                : 0)
            + (warningsCount > 0 ? warningsCount * 20 : 0)
            + fieldSectionsHeight
            + (groupCount > 0 ? (groupCount - 1) * 12 : 0)
            // Collapsed declinations toggle / empty-state line under "Admitted declinations" (when present).
            // Expanding the carrier list grows past this; the panel scrolls internally in that case.
            + ((row.fields || []).some((f) => f.key === 'declinationCount') ? 22 : 0)
            + 32;
    }, []);

    /** Keeps the read-only grid's Coverage column in sync when the coverage code is changed via the
     *  picker in the expand panel (the field itself is updated by handleRowFieldChange). */
    const handleRowCoverageChange = (extractedDocumentDataId: number, code: string) => {
        setPolicies((prev) => prev.map((row) => (
            row.extractedDocumentDataId === extractedDocumentDataId ? { ...row, coverageCode: code } : row
        )));
    };

    const handleViewExtraction = (row: BatchReviewRow) => {
        const segment = stateSegment(row.state);
        if (!segment) return;
        navigate(`${AppRoutes.filingQueue.filingReview}/${segment}/${row.customerPolicyId}`);
    };

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerDocs, setViewerDocs] = useState<DocumentViewerItem[]>([]);
    const [viewerLoadingId, setViewerLoadingId] = useState<number | null>(null);

    const handleViewDocument = async (row: BatchReviewRow, page?: number | null) => {
        setViewerLoadingId(row.extractedDocumentDataId);
        try {
            const query = new URLSearchParams({ customerPolicyId: String(row.customerPolicyId), state: row.state });
            // tokenRef, not `token`: this runs from the expand panel's frozen handlers, so the captured
            // `token` is the first-render one and 401s after any mid-session refresh.
            const res: any = await apiClient(`/AutomaticFiling/DocumentUrl?${query.toString()}`, {
                headers: { Authorization: `Bearer ${tokenRef.current}` },
            });
            const url = res?.data?.sourceDocumentUrl;
            if (!url) return;
            setViewerDocs([{ documentUrl: url, documentName: row.documentName || `${row.policyNumber || 'document'}.pdf`, page: page ?? null }]);
            setViewerOpen(true);
        } finally {
            setViewerLoadingId(null);
        }
    };

    /** Shared build+download: POSTs a state's build-batch with ?download=1 (streams the built file as an
     *  attachment) for either a single policy ({customerPolicyId}) or the whole batch ({customerPolicyIds}).
     *  A failed build returns a JSON report instead of the file — surface its errors rather than saving an
     *  error page to disk. */
    const buildAndDownload = async (segment: string, body: Record<string, unknown>, fallbackName: string): Promise<void> => {
        const res = await fetch(`${config}/AutomaticFiling/${segment}/build-batch?download=1`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenRef.current}` },
            body: JSON.stringify(body),
        });
        const contentType = res.headers.get('Content-Type') || '';
        if (!res.ok || contentType.includes('application/json')) {
            let errors: string[] = [`HTTP ${res.status}`];
            try {
                const j = await res.json();
                const rpt = j?.data?.report ?? j?.report;
                errors = Array.isArray(rpt?.errors) && rpt.errors.length ? rpt.errors : [j?.message || `HTTP ${res.status}`];
            } catch { /* keep default */ }
            await Swal.fire({
                icon: 'error',
                title: 'Batch file not built',
                html: `<div style="text-align:left;font-size:13.5px">Resolve these before the file can be built:<ul style="margin:8px 0 0 18px">${errors.map((e) => `<li style="margin-bottom:4px">${escapeHtml(e)}</li>`).join('')}</ul></div>`,
                confirmButtonColor: '#2099d2',
            });
            return;
        }
        const blob = await res.blob();
        const cd = res.headers.get('Content-Disposition') || '';
        const m = /filename="?([^";]+)"?/i.exec(cd);
        saveAs(blob, m ? m[1] : fallbackName);
    };

    /** Build + download a single policy's filing file (checklist #3, per-policy expand panel). */
    const handleDownloadBatch = async (row: BatchReviewRow) => {
        // Through the ref — see stateSegmentRef. Called from the expand panel, whose handlers are frozen
        // at first render, so the captured `stateSegment` would always resolve null here.
        const segment = stateSegmentRef.current(row.state);
        if (!segment) {
            await Swal.fire({
                icon: 'info',
                title: 'Not a filing-enabled state',
                text: `${row.state || 'This policy’s state'} has no filing configuration, so there is no batch file to build for it.`,
                confirmButtonColor: '#2099d2',
            });
            return;
        }
        await buildAndDownload(segment, { customerPolicyId: row.customerPolicyId }, `${row.policyNumber || 'filing'}-${segment}.xml`);
    };

    // One combined batch file for the whole review — every ready-to-file policy in this client+state
    // batch, as the single artifact that gets submitted to the state. Requires the build-batch handler to
    // accept customerPolicyIds[] (sibling internal-api PR); until then it fails loud with the report.
    const [batchDownloadBusy, setBatchDownloadBusy] = useState(false);
    const handleDownloadFullBatch = async () => {
        const segment = selectedState ? stateSegment(selectedState.stateName) : null;
        if (!segment) return;
        // Only policies still awaiting a filing — a queued/filed policy must not be rebuilt into a batch
        // that could be uploaded to the state a second time.
        const ids = policies.filter(isRowFilable).map((p) => p.customerPolicyId);
        if (!ids.length) {
            await Swal.fire({ icon: 'info', title: 'Nothing ready to download', text: 'No policies are ready to file in this batch.', confirmButtonColor: '#2099d2' });
            return;
        }
        setBatchDownloadBusy(true);
        try {
            await buildAndDownload(segment, { customerPolicyIds: ids }, `BatchFiling_${selectedPartner?.customerName || 'Client'}_${selectedState?.stateName || 'State'}.xml`);
        } finally {
            setBatchDownloadBusy(false);
        }
    };

    /** PROOF BATCH — build and download the artifact that WOULD have gone to the state for the checked
     *  Manually Filed policies, so a filer can inspect what a hand-filing should have contained.
     *
     *  Two things make this safe, and both are load-bearing:
     *
     *   1. IT REUSES THE REAL BUILDER. It POSTs the same `/build-batch?download=1` endpoint the normal
     *      batch download uses, which routes to buildBatchMulti → the state's own adapter. There is
     *      deliberately no second batch-construction path here: an artifact built by different code
     *      would prove nothing about what the real filing would look like, which is the entire point.
     *   2. IT CANNOT FILE. `/build-batch` reaches no state — it is the read+build half of the pipeline
     *      and is explicitly not one of the transmitting actions. The submit endpoints additionally
     *      refuse a manually filed policy server-side, so this stays inspection-only even if someone
     *      pointed it at a different route.
     *
     *  Grouped by each row's OWN state (a build is per client+state), so a selection spanning states
     *  downloads one file per state rather than silently building only the first. */
    const [proofBatchBusy, setProofBatchBusy] = useState(false);
    const handleDownloadProofBatch = async () => {
        const rows = selectedProofRows;
        if (!rows.length) return;

        // Grouping + the eligibility re-filter live in the lifecycle helper so they are unit-testable
        // (see planProofBatch): the guarantee that a proof batch contains ONLY manually filed policies
        // is not something a DataGrid test could establish.
        const { groups, unresolved } = planProofBatch(rows, stateSegment);

        if (groups.length === 0) {
            await Swal.fire({
                icon: 'info',
                title: 'No filing state resolved',
                html: `<div style="text-align:left;font-size:13.5px">None of the selected policies has a state that is set up for automated filing, so there is no batch to build:<ul style="margin:8px 0 0 18px">${unresolved.map((r) => `<li style="margin-bottom:4px">${escapeHtml(describeRow(r))} — state <b>${escapeHtml(r.state || 'not set')}</b></li>`).join('')}</ul></div>`,
                confirmButtonColor: '#2099d2',
            });
            return;
        }

        setProofBatchBusy(true);
        try {
            for (const g of groups) {
                await buildAndDownload(
                    g.segment,
                    { customerPolicyIds: g.customerPolicyIds },
                    `ProofBatch_NOT_FILED_${selectedPartner?.customerName || 'Client'}_${g.stateName}.xml`,
                );
            }
        } finally {
            setProofBatchBusy(false);
        }

        if (unresolved.length) {
            await Swal.fire({
                icon: 'warning',
                title: 'Some policies were skipped',
                html: `<div style="text-align:left;font-size:13.5px">These have no filing-enabled state, so they are not in the downloaded file(s):<ul style="margin:8px 0 0 18px">${unresolved.map((r) => `<li style="margin-bottom:4px">${escapeHtml(describeRow(r))}</li>`).join('')}</ul></div>`,
                confirmButtonColor: '#2099d2',
            });
        }
    };

    // "Download all (zip)" — one archive with a built batch file per (client, state) for every
    // extracted-and-sent policy in the current Client/State filter (each client is its own filer, so
    // batches are never merged across clients). Backend returns a zip on success, or JSON with a
    // per-(client,state) failure report otherwise — surface that rather than saving an error page.
    const [allZipBusy, setAllZipBusy] = useState(false);
    const handleDownloadAllZip = async () => {
        setAllZipBusy(true);
        try {
            const body: Record<string, unknown> = {};
            if (selectedPartner?.partnersId) body.partnersId = selectedPartner.partnersId;
            if (selectedState?.stateId) body.stateId = selectedState.stateId;
            // If rows are checked, scope the zip to exactly those policies (mapped from the grid's
            // extractedDocumentDataId to customerPolicyId, deduped — one policy can have several
            // extraction rows); otherwise it's the whole current filter. Reads the same
            // visible-and-checked set the filing action uses, so the button's count and its payload
            // can't disagree.
            if (selectedVisibleRows.length > 0) {
                body.customerPolicyIds = Array.from(new Set(selectedVisibleRows.map((p) => p.customerPolicyId)));
            }
            const res = await fetch(`${config}/AutomaticFiling/build-all-batches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenRef.current}` },
                body: JSON.stringify(body),
            });
            const contentType = res.headers.get('Content-Type') || '';
            if (!res.ok || contentType.includes('application/json')) {
                let msg = `HTTP ${res.status}`;
                let failedList: string[] = [];
                try {
                    const j = await res.json();
                    msg = j?.message || msg;
                    const failed = j?.data?.report?.failed;
                    if (Array.isArray(failed)) failedList = failed.map((f: any) => `${f.client} (${f.state}): ${(f.errors || []).join('; ')}`);
                } catch { /* keep default */ }
                await Swal.fire({
                    icon: 'error',
                    title: 'Could not build batches',
                    html: `<div style="text-align:left;font-size:13.5px">${escapeHtml(msg)}${failedList.length ? `<ul style="margin:8px 0 0 18px">${failedList.map((e) => `<li style="margin-bottom:4px">${escapeHtml(e)}</li>`).join('')}</ul>` : ''}</div>`,
                    confirmButtonColor: '#2099d2',
                });
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const m = /filename="?([^";]+)"?/i.exec(cd);
            saveAs(blob, m ? m[1] : 'BatchFilings.zip');
        } catch (_e) {
            await Swal.fire({ icon: 'error', title: 'Could not build batches', text: 'Something went wrong building the zip — try again.', confirmButtonColor: '#2099d2' });
        } finally {
            setAllZipBusy(false);
        }
    };

    /** Re-run extraction for this policy (checklist #4) via the generic engine trigger, then reload the
     *  batch so the freshly-extracted values, gaps, and readiness refresh. Uses the existing
     *  the internal-api's dedicated RATE-LIMITED re-extract endpoint (per-policy cooldown + a global
     *  token bucket) so a filer re-clicking can't hammer the LLM extraction engine. */
    const handleReExtract = async (row: BatchReviewRow) => {
        // Prefer the state's postal code from the capability registry (covers every filing-enabled state);
        // fall back to the row's own state string only if the registry lookup misses.
        const stateCode = statesRef.current.find((s) => s.stateId === row.stateId)?.postalAbbv
            || toStateCode(row.state);
        // Plain JSON POST → the standard apiClient wrapper (centralized base URL + Sentry + idle-timer).
        // On success apiClient returns the camelCased body ({ status:true, ... }); on an HTTP error it
        // returns { ok:false, ...body } — and the failure body's `status:false` clobbers the numeric HTTP
        // status, so a THROTTLE (429) is detected by its `data.retryAfterSeconds`, which only that path carries.
        const res: any = await apiClient('/AutomaticFiling/reExtract', {
            method: 'POST',
            headers: { Authorization: `Bearer ${tokenRef.current}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ customerPolicyId: row.customerPolicyId, state: stateCode }),
        });
        // Throttled — NOT a failure. Tell the filer to wait (the backend message already names the seconds).
        const retryAfter = res?.data?.retryAfterSeconds;
        if (retryAfter != null) {
            await Swal.fire({
                icon: 'info',
                title: 'Please wait',
                text: res?.message || `This policy was just re-extracted — try again in ${retryAfter}s.`,
                confirmButtonColor: '#2099d2',
            });
            return;
        }
        // Genuine failure (network / 4xx / 5xx / engine error).
        if (!res || res.ok === false || res.status === false) {
            await Swal.fire({
                icon: 'error',
                title: 'Re-extract failed',
                text: res?.message || 'Re-extract failed — please try again.',
                confirmButtonColor: '#2099d2',
            });
            return;
        }
        // Success: reload the batch in place so the row + its "Extracted just now" reflect the new run.
        setReloadNonce((n) => n + 1);
        // If the engine truncated an oversized PDF (>100 pages), warn the filer explicitly and BLOCK on
        // it — the extraction only saw the first 100 pages, so it must be verified, not auto-dismissed.
        const truncated = truncationWarningText(res?.data);
        if (truncated) {
            await Swal.fire({
                icon: 'warning',
                title: 'Large document — first 100 pages read',
                text: truncated,
                confirmButtonColor: '#2099d2',
            });
            return;
        }
        // Otherwise a brief auto-dismiss toast confirms it without blocking a filer doing several back-to-back.
        void Swal.fire({
            icon: 'success',
            title: 'Re-extraction complete',
            text: 'This policy was updated with the latest extraction.',
            timer: 1800,
            showConfirmButton: false,
        });
    };

    /** Navigate to the Client Filing Defaults page, carrying the current client + state so the "Back to
     *  Batch Filing Review" link returns to the same batch. */
    const goToClientDefaults = () => {
        const params = new URLSearchParams();
        if (selectedPartner?.partnersId) params.set('partnersId', String(selectedPartner.partnersId));
        if (selectedState?.stateId) params.set('stateId', String(selectedState.stateId));
        const qs = params.toString();
        navigate(`${AppRoutes.filingQueue.clientDefaults}${qs ? `?${qs}` : ''}`);
    };

    /** Commits an edit made to a field inside a row's expand panel — mirrors handleCellEditCommit's
     *  "mutate the row in policies state" pattern, just reaching into the row's `fields` array instead
     *  of a top-level column. No separate overrides/original-value diff (unlike the old FilingReview.tsx)
     *  since there's no batch-submit endpoint yet to send an overrides payload to. */
    const handleRowFieldChange = (extractedDocumentDataId: number, key: string, value: string) => {
        setPolicies((prev) => prev.map((row) => {
            if (row.extractedDocumentDataId !== extractedDocumentDataId) return row;
            return {
                ...row,
                fields: row.fields.map((f) => (f.key === key ? { ...f, value } : f)),
            };
        }));
    };

    // Minimal, read-only, scannable column set (Zach's "simpler the better"). Transaction Type, Status,
    // Physical Risk Address, Extraction/Due dates, the standalone Details button and all inline editing
    // have moved into the expand panel — the grid is now a clean overview and the expand is the work
    // surface. Coverage is display-only here; it's edited via the picker in the expand's Computed group.
    const columns: GridColDef[] = useMemo(() => [
        // Columns match the Ready-to-File queue (MISC.csv): Filing Due Date · Client · Invoice # ·
        // Insured · Policy # · Home State · Transaction Type · Invoice Date · Policy Effective/Expiration
        // Date · Gross Premium · Status — with Filing Status pulled to the front (represents Status) and
        // Coverage added (Jerry, 2026-07-16).
        { field: 'filingStatus', headerName: 'Filing Status', flex: 1, sortable: false, filterable: false,
            renderCell: (params) => <FilingStatusCell row={params.row as BatchReviewRow} />,
        },
        // BATCH DATE — the day this policy became part of a filing batch. Rendered through
        // batchDateLabel, which is the ONE place that decides between a real date, "Not recorded" and
        // "Filed manually". Deliberately not a plain value column: a blank cell would read as "no batch
        // happened" when the truth is that we were not recording yet, and a manually-filed policy's
        // Filed date must never appear here unlabelled.
        { field: 'batchDate', headerName: 'Batch Date', flex: 0.85, sortable: false, filterable: false,
            renderCell: (params) => {
                // Formatted with the grid's OWN date formatter, the same one Filing Due Date and the
                // policy dates use — a Batch Date reading 2026-03-08 next to a due date reading 03-08-2026
                // reads as a different kind of value.
                const label = batchDateLabel(params.row as BatchReviewRow, formatFilingDate);
                const muted = label.primary === 'Not recorded' || label.primary === 'Filed manually';
                return (
                    <Box sx={{ lineHeight: 1.2, py: 0.25 }}>
                        <Typography variant="body2" sx={{ color: muted ? '#94a3b8' : 'inherit', fontStyle: muted ? 'italic' : 'normal' }}>
                            {label.primary}
                        </Typography>
                        {label.secondary && (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{label.secondary}</Typography>
                        )}
                    </Box>
                );
            },
        },
        { field: 'dueDate', headerName: 'Filing Due Date', flex: 0.9,
            valueFormatter: (params) => (params.value ? formatFilingDate(params.value as string) : ''),
        },
        { field: 'client', headerName: 'Client', flex: 1 },
        { field: 'invoiceNumber', headerName: 'Invoice Number', flex: 0.8 },
        { field: 'insuredName', headerName: 'Insured Name', flex: 1.1 },
        { field: 'policyNumber', headerName: 'Policy Number', flex: 0.9 },
        { field: 'state', headerName: 'Home State', flex: 0.55 },
        { field: 'transactionType', headerName: 'Transaction Type', flex: 0.9 },
        { field: 'invoiceDate', headerName: 'Invoice Date', flex: 0.8,
            valueFormatter: (params) => (params.value ? formatFilingDate(params.value as string) : ''),
        },
        { field: 'policyEffectiveDate', headerName: 'Policy Effective Date', flex: 0.85,
            valueFormatter: (params) => (params.value ? formatFilingDate(params.value as string) : ''),
        },
        { field: 'policyExpirationDate', headerName: 'Policy Expiration Date', flex: 0.85,
            valueFormatter: (params) => (params.value ? formatFilingDate(params.value as string) : ''),
        },
        { field: 'premium', headerName: 'Gross Premium', flex: 0.8, align: 'right', headerAlign: 'right',
            valueFormatter: (params) => (params.value != null ? formatPrice(params.value as number) : ''),
        },
        { field: 'coverageCode', headerName: 'Coverage', flex: 0.8,
            renderCell: (params) => (
                <CoverageCodeCell row={params.row as BatchReviewRow} value={params.value as string | null} />
            ),
        },
        { field: 'viewDocument', headerName: 'Action', flex: 0.5, sortable: false, filterable: false,
            align: 'right', headerAlign: 'right',
            renderCell: (params) => {
                const row = params.row as BatchReviewRow;
                const isLoading = viewerLoadingId === row.extractedDocumentDataId;
                return (
                    <Tooltip title="View document">
                        <span>
                            <IconButton
                                size="small"
                                disabled={isLoading}
                                onClick={() => handleViewDocument(row)}
                                sx={{ color: '#15607a' }}
                            >
                                {isLoading ? <CircularProgress size={18} /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [stateSegment, viewerLoadingId]);

    const hasSelection = Boolean(selectedPartner?.partnersId && selectedState?.stateId);

    const totals = useMemo(() => {
        // WHAT THE TOTALS COUNT. Base is the current view — the lifecycle tab plus the insured-name /
        // policy-number filters — because that is the set the filer is looking at. But the moment rows
        // are CHECKED, the totals describe just those: a filer selecting one row to sanity-check its
        // premium before filing wants that row's number, not the tab's. Selection wins, and clearing
        // the checkboxes returns the totals to the whole view.
        //
        // selectedVisibleRows (not raw selectedRows) so the totals count exactly the set the File
        // button would submit — it is already pruned to rows still in the filter and still filable.
        const scope = selectedVisibleRows.length > 0 ? selectedVisibleRows : visiblePolicies;
        // toMoneyNumber, NOT `|| 0`: a currency field can arrive as a string (the API sources these from
        // nvarchar columns), and one string turns `+` into concatenation — the whole running total then
        // becomes digit-soup that formatPrice happily renders as a plausible figure. See the helper.
        const totalPremium = scope.reduce((sum, r) => sum + toMoneyNumber(r.premium), 0);
        const totalTax = scope.reduce((sum, r) => sum + toMoneyNumber(r.tax), 0);
        const totalsScopedToSelection = selectedVisibleRows.length > 0;
        // coverageCodes / exceptionCount stay on the whole view deliberately: they describe the BATCH a
        // filer is working through, not the row under inspection, and narrowing them to a checkbox
        // would hide exceptions sitting elsewhere in the tab.
        const coverageCodes = Array.from(new Set(visiblePolicies.map((r) => r.coverageCode).filter((c): c is string => Boolean(c))));
        const exceptionCount = visiblePolicies.filter((r) => r.hasException).length;
        return { totalPremium, totalTax, totalsScopedToSelection, selectedCount: selectedVisibleRows.length, coverageCodes, exceptionCount };
    }, [visiblePolicies, selectedVisibleRows]);

    const handleExport = () => {
        // "Filing Status" is the LIFECYCLE position, and it is a separate column from "Status" on
        // purpose: `r.status` is a different backend field and never carried the lifecycle vocabulary,
        // so the export could not tell a policy WE filed through the API from one already filed by hand
        // in the state portal — both simply appeared under whatever `status` said. It uses the same
        // statusChipStyle label the grid shows, so the spreadsheet and the screen cannot disagree.
        // ⚠️ Dates are formatted in buildBatchReviewExportRows, NOT here. This used to pass
        // `r.extractionDate` and `r.dueDate` straight through, so both landed in the sheet as raw ISO
        // strings — Due Date as `2026-08-31T00:00:00.000Z`, which is what QA reported. Due Date is a
        // business date and loses its time; Extraction Date is a real instant and keeps it.
        const rows = buildBatchReviewExportRows(
            policies,
            (r) => statusChipStyle(rowFilingStatus(r as BatchReviewRow)).label,
        );
        const fileName = `BatchFilingReview_${selectedPartner?.customerName || 'Client'}_${selectedState?.stateName || 'State'}.xlsx`;
        writeXlsxFile([[...BATCH_REVIEW_EXPORT_HEADERS], ...rows], fileName);
    };

    const [fileBatchConfirmOpen, setFileBatchConfirmOpen] = useState(false);
    const [filingPlan, setFilingPlan] = useState<FilingPlan | null>(null);
    const [filingBusy, setFilingBusy] = useState(false);
    // Per-client filing-defaults pop-out (checklist #8) — the same editor as the standalone admin page,
    // scoped to the currently-selected client.
    const [clientDefaultsOpen, setClientDefaultsOpen] = useState(false);

    /** Builds the filing plan from the rows that are BOTH currently visible AND checked, then opens the
     *  confirm dialog. The plan it stores is the literal payload — `handleConfirmFileBatch` sends the
     *  ids in `plan.groups` and nothing else — so what the dialog lists and what leaves the browser
     *  cannot drift apart.
     *
     *  Bugs this replaces, all reproduced live:
     *   1. the handler ignored `selectedRows` entirely and filed every ready row in `policies`, so
     *      "File 1 policy" filed three — across four different clients in an unfiltered view;
     *   2. it early-returned when no State was picked, producing a click that did nothing at all;
     *   3. it read the unfiltered `policies`, so rows the filer had filtered or paged away from were
     *      still submitted (see `selectedVisibleRows`);
     *   4. it mapped rows straight to ids without deduping, so a policy with two extraction rows could
     *      be sent twice in one payload.
     *
     *  Every path out of here ends in a dialog — including an unexpected throw. */
    const handleFileBatchClick = async () => {
        try {
            // The single derived set: visible under the current filter, on the current page, checked,
            // and still filable.
            const rows = selectedVisibleRows;

            if (!rows.length) {
                // THREE different reasons the filing set can be empty, and they need different words.
                // The third is new: checkboxes now also serve inspection, so a filer can have Manually
                // Filed rows checked and nothing filable. Telling them their selection "is no longer
                // available" would be plainly false — those rows are right there, they simply are not
                // filable and never will be.
                const proofOnly = selectedProofRows.length > 0;
                const staleCount = selectedRows.length;
                await Swal.fire({
                    icon: 'info',
                    title: proofOnly
                        ? 'Manually filed policies cannot be filed'
                        : (staleCount ? 'That selection is no longer available' : 'Nothing selected'),
                    text: proofOnly
                        ? 'The policies you checked are already on file with the state — they were filed by hand in the state portal, so filing them here would duplicate them with the regulator. Use Download proof batch to inspect what would have been sent.'
                        : (staleCount
                            ? 'The policies you had checked are no longer shown or are no longer ready to file — the batch changed. Check the policies you want to file and try again.'
                            : 'Check the policies you want to file, then click File policies. Only policies that are ready to file can be selected.'),
                    confirmButtonColor: '#2099d2',
                });
                return;
            }

            // Group by the row's OWN state (not a screen-level State picker) so a filer can select
            // policies across many states and file them in one action without filtering first.
            //
            // DEDUPE BY customerPolicyId, globally. Grid rows are keyed by extractedDocumentDataId, so
            // one policy with several extraction rows can be checked more than once — which would send
            // that policy twice in a single payload, or once to each state if the duplicate rows
            // disagree about `state`. The server ledger would likely catch it as a conflict, but the
            // client must not generate a double-file attempt in the first place. First row wins.
            const groups = new Map<string, FilingStateGroup>();
            const unresolved: BatchReviewRow[] = [];
            const duplicates: BatchReviewRow[] = [];
            const claimedPolicyIds = new Set<number>();
            for (const row of rows) {
                if (claimedPolicyIds.has(row.customerPolicyId)) { duplicates.push(row); continue; }
                const segment = stateSegment(row.state);
                if (!segment) { unresolved.push(row); continue; }
                claimedPolicyIds.add(row.customerPolicyId);
                const existing = groups.get(segment);
                if (existing) existing.rows.push(row);
                else groups.set(segment, { segment, stateName: row.state || segment, rows: [row] });
            }

            if (groups.size === 0) {
                await Swal.fire({
                    icon: 'error',
                    title: 'No filing state resolved',
                    html: `<div style="text-align:left;font-size:13.5px">${extractionStates.length === 0
                        ? 'The list of filing-enabled states has not loaded yet, so none of the selected policies can be routed. Reload the page and try again.'
                        : 'None of the selected policies has a state that is set up for automated filing:'}<ul style="margin:8px 0 0 18px">${unresolved.map((r) => `<li style="margin-bottom:4px">${escapeHtml(describeRow(r))} — state <b>${escapeHtml(r.state || 'not set')}</b></li>`).join('')}</ul></div>`,
                    confirmButtonColor: '#2099d2',
                });
                return;
            }

            const clients = Array.from(new Set(Array.from(groups.values())
                .flatMap((g) => g.rows)
                .map((r) => r.client)
                .filter((c): c is string => Boolean(c))));

            setFilingPlan({
                groups: Array.from(groups.values()).sort((a, b) => a.stateName.localeCompare(b.stateName)),
                unresolved,
                duplicates,
                clients,
            });
            setFileBatchConfirmOpen(true);
        } catch (e: unknown) {
            // Never exit silently. A throw here (a malformed row, a helper blowing up) used to leave the
            // filer with a click that did nothing — the exact symptom this whole branch exists to kill.
            await Swal.fire({
                icon: 'error',
                title: 'Could not prepare the filing',
                text: e instanceof Error ? e.message : 'Something went wrong working out what to file. Reload the page and try again.',
                confirmButtonColor: '#2099d2',
            });
        }
    };

    /** Fans the confirmed plan out over the per-state submit endpoint — one request per state, run
     *  sequentially, with a failure in one state NEVER aborting the rest. Results are aggregated into a
     *  single dialog that reports each state's real outcome (filed vs. queued vs. rejected vs. error)
     *  instead of the old blanket "Filed to the state". */
    const handleConfirmFileBatch = async () => {
        const plan = filingPlan;
        setFileBatchConfirmOpen(false);
        if (!plan) return;

        setFilingBusy(true);
        setBatchDownloadBusy(true);
        const results: StateSubmitResult[] = [];
        try {
            for (const group of plan.groups) {
                // Deduped at plan time; deduped again here so no future edit to the grouping can put
                // the same policy in one payload twice.
                const ids = Array.from(new Set(group.rows.map((r) => r.customerPolicyId)));
                try {
                    const res = await fetch(`${config}/AutomaticFiling/${group.segment}/submit-batch`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenRef.current}` },
                        body: JSON.stringify({ customerPolicyIds: ids, confirm: true }),
                    });
                    let body: { status?: boolean; message?: string; data?: SubmitBatchData } | null = null;
                    try { body = await res.json(); } catch { /* non-JSON error page */ }
                    const data: SubmitBatchData = body?.data ?? {};
                    const outcome = deriveStateOutcome(res, body, data);
                    const succeeded = outcome === 'filed' || outcome === 'queued' || outcome === 'manual';
                    results.push({
                        stateName: group.stateName,
                        segment: group.segment,
                        requestedIds: ids,
                        outcome,
                        httpStatus: res.status,
                        data,
                        errors: succeeded ? [] : failureReasons(data, body, res.status),
                    });
                } catch (e: unknown) {
                    // Transport failure: the request may or may not have reached the state. Treated as
                    // "possibly in flight" in the dialog, never as a clean failure to retry blindly.
                    const message = e instanceof Error ? e.message : 'The filing request could not be completed.';
                    results.push({
                        stateName: group.stateName,
                        segment: group.segment,
                        requestedIds: ids,
                        outcome: 'transport',
                        httpStatus: null,
                        data: {},
                        errors: [message],
                    });
                }
            }
        } finally {
            setFilingBusy(false);
            setBatchDownloadBusy(false);
        }

        const rowByPolicyId = new Map<number, BatchReviewRow>();
        for (const group of plan.groups) for (const r of group.rows) rowByPolicyId.set(r.customerPolicyId, r);

        const summary = summarizeFilingResults(results);
        await Swal.fire({
            icon: summary.icon,
            title: summary.title,
            html: `<div style="text-align:left;font-size:13.5px">${results.map((r) => renderStateResultHtml(r, rowByPolicyId)).join('')}${renderHeldBackHtml(plan)}</div>`,
            width: 640,
            confirmButtonColor: '#2099d2',
        });

        // Manual-upload states keep the existing fallback: build the batch file for a portal upload.
        // Done AFTER the summary so its own failure dialog can't interleave with the results.
        for (const r of results.filter((x) => x.outcome === 'manual')) {
            await buildAndDownload(
                r.segment,
                { customerPolicyIds: r.requestedIds },
                `BatchFiling_${r.stateName.replace(/\s+/g, '')}.xml`,
            );
        }

        setSelectedRows([]);
        setFilingPlan(null);
        setReloadNonce((n) => n + 1);
    };

    return (
        <Box>
            <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 9990, width: '100%' }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: '#132D3D' }} />}
            </div>

            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#fafbfc',
                        minHeight: 56,
                        marginLeft: { xs: '-24px', md: '0px' },
                        marginRight: { xs: '-24px', md: '0px' },
                    }}
                >
                    <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: '#0f172a' }}>
                        Batch Filing Review
                        {selectedPartner?.customerName ? ` — ${selectedPartner.customerName}` : ''}
                        {selectedState?.stateName ? ` — ${selectedState.stateName}` : ''}
                    </h4>
                    <Box sx={{ width: { xs: '100%', md: 'auto' }, display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
                        {/* Per-client defaults (checklist #8): a pop-out for the selected client, plus a link
                            to the full admin page. Both edit the same carrier / coverage / license codes. */}
                        {selectedPartner && (
                            <Button variant="outlined"
                                onClick={() => setClientDefaultsOpen(true)}
                                sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}
                            >Client filing defaults</Button>
                        )}
                        <Button variant="text"
                            onClick={goToClientDefaults}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#2099d2' }}
                        >Manage all clients ↗</Button>
                        {/* File policies — files EXACTLY the checked rows, grouped by each row's own
                            state (no State picker required). Never disabled on "nothing selected": the
                            handler explains it in a dialog rather than the click doing nothing.
                            Download batch file builds ONE client+state batch, so it needs a
                            Client+State selected. Download all (zip) builds every (client, state) batch
                            in the current filter as one archive. */}
                        {/* Disabled while the batch is RELOADING as well as while filing: mid-reload the
                            header already shows the new Client/State but `policies` still holds the old
                            filter's rows, and clicking then filed a client the filer was no longer
                            looking at. */}
                        <Button variant="contained"
                            disabled={filingBusy || loading}
                            onClick={handleFileBatchClick}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                flexShrink: 0,
                                backgroundColor: '#2e7d32',
                                color: WhiteColor,
                                px: 2,
                                '&:hover': { backgroundColor: '#1b5e20' },
                                '&.Mui-disabled': { backgroundColor: 'rgba(46, 125, 50, 0.24)', color: 'rgba(0, 0, 0, 0.38)' },
                            }}
                        >
                            {/* Counts the SAME set that gets filed — see `selectedVisibleRows`. */}
                            {filingBusy
                                ? 'Filing…'
                                : selectedVisibleRows.length > 0
                                    ? `File ${selectedVisibleRows.length} ${selectedVisibleRows.length === 1 ? 'policy' : 'policies'}`
                                    : 'File policies'}
                        </Button>
                        {hasSelection && (
                            <Button variant="outlined"
                                onClick={handleDownloadFullBatch}
                                disabled={batchDownloadBusy || loading}
                                startIcon={<FileDownload />}
                                title="Builds one file for every ready-to-file policy in this client + state batch"
                                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, px: 2, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}
                            >{batchDownloadBusy ? 'Building…' : 'Download batch file'}</Button>
                        )}
                        {/* PROOF BATCH — inspection only, never a filing. Shown only when Manually Filed
                            rows are checked, so it cannot be mistaken for the filing action next to it,
                            and worded to say what it does NOT do as much as what it does. */}
                        {selectedProofRows.length > 0 && (
                            <Button variant="outlined"
                                onClick={handleDownloadProofBatch}
                                disabled={proofBatchBusy}
                                startIcon={<FileDownload />}
                                title="Builds the batch file that WOULD have been sent to the state for the selected Manually Filed policies, and downloads it for inspection. Nothing is submitted."
                                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, px: 2, borderColor: '#c4b5e6', color: '#5b3fa6', '&:hover': { borderColor: '#5b3fa6', backgroundColor: '#f6f3fc' } }}
                            >
                                {proofBatchBusy
                                    ? 'Building…'
                                    : `Download proof batch (${selectedProofRows.length})`}
                            </Button>
                        )}
                        {/* Disabled mid-reload for the same reason as File: both of these build their
                            output from `policies`, which still holds the PREVIOUS scope's rows until the
                            new batch lands, while the header and the generated filename already name the
                            new one. A ZIP or a spreadsheet handed over in that window quietly describes a
                            different client than it claims to. */}
                        <Button variant="outlined"
                            onClick={handleDownloadAllZip}
                            disabled={allZipBusy || loading}
                            startIcon={<FileDownload />}
                            title={selectedVisibleRows.length > 0
                                ? 'Builds a batch file per client + state for the SELECTED policies and downloads them as one ZIP.'
                                : 'Builds a batch file per client + state for every extracted-and-sent policy in the current Client/State filter and downloads them as one ZIP. Check rows to limit it to a selection.'}
                            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, px: 2, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}
                        >{allZipBusy ? 'Zipping…' : (selectedVisibleRows.length > 0 ? `Download ${selectedVisibleRows.length} selected (zip)` : 'Download all (zip)')}</Button>
                        <Button variant="contained"
                            onClick={handleExport}
                            disabled={loading}
                            startIcon={<FileDownload />}
                            sx={{ ...FILING_QUEUE_BLUE_BUTTON_SX, flexShrink: 0, px: 2 }}
                        >Export to Excel</Button>
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                width: '100%',
                borderRadius: '16px',
                marginTop: '15px',
                border: '1px solid rgba(22, 46, 60, 0.10)',
                backgroundColor: WhiteColor,
                boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
                p: { xs: 1.5, sm: 2.5 },
            }}>
                <Grid container spacing={2} className="filter-grid" display="flex" alignItems="center" sx={{ mb: 2.5 }}>
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                sx={{
                                    marginTop: '-4px !important',
                                    '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                }}
                                options={partners}
                                getOptionLabel={(option) => option.customerName}
                                isOptionEqualToValue={(a, b) => a.partnersId === b.partnersId}
                                value={selectedPartner}
                                onChange={(_event, value) => {
                                    // Re-picking the client already selected is not a scope change, and
                                    // must not be treated as one: MUI fires onChange for it, and clearing
                                    // the Batch Date there would throw away a filer's chosen day for a
                                    // no-op. Bailing early also skips a reload that would return the same
                                    // rows. Matches the Autocomplete's own isOptionEqualToValue above.
                                    if (value?.partnersId === selectedPartner?.partnersId) return;
                                    // This used to also run `setSelectedState(null)`, which threw a filer
                                    // working one state back to All States on every client change. The
                                    // State options are NOT client-scoped (they come from the filing
                                    // capability registry), so the selection stays valid; the Batch Date
                                    // is the value that can genuinely stop existing, and it is the one
                                    // this now clears. See ../helper/batchReviewFilters.
                                    applyFilterValues(filterValuesOnClientChange(currentFilterValues, value));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Client"
                                        size="small"
                                        sx={{
                                            '& .MuiInputBase-root': { marginTop: '5px !important' },
                                            '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                            '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                sx={{
                                    marginTop: '-4px !important',
                                    '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                }}
                                options={states}
                                getOptionLabel={(option) => option.stateName}
                                isOptionEqualToValue={(a, b) => a.stateId === b.stateId}
                                value={selectedState}
                                // Clears the Batch Date for the same reason the Client change does:
                                // `filingBatchDates` is queried on partnersId AND stateId, so narrowing
                                // the state can strand the selected day just as changing the client can.
                                onChange={(_event, value) => {
                                    // Same no-op guard as the Client picker above.
                                    if (value?.stateId === selectedState?.stateId) return;
                                    applyFilterValues(filterValuesOnStateChange(currentFilterValues, value));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="State"
                                        size="small"
                                        sx={{
                                            '& .MuiInputBase-root': { marginTop: '5px !important' },
                                            '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                            '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    {/* Quick filters (Kelly, 2026-07-14): narrow the loaded batch by insured name / policy
                        number — same columns the Filing Queue filters on.
                        ⚠️ RENDERED UNCONDITIONALLY, and that is the point. None of these three controls
                        needs a single row of data to be correct — an empty Insured Name box is an empty
                        Insured Name box before and after the batch lands — so holding them back until a
                        ~3-second request returns bought nothing and made the screen look broken for the
                        whole of it. They used to be gated on `policiesLoaded`, which unmounted them for
                        the duration of every REFETCH (the Batch Date Select tore down its own open
                        popover the instant an option was picked); that was fixed by moving them to the
                        `hasLoadedOnce` latch, and this removes the last version of the same problem —
                        the first load. The latch's guarantee is strictly strengthened, not weakened:
                        controls that are never gated can never be unmounted by a load.
                        The Batch Date Select simply offers "All batch dates" until its day list arrives,
                        which is what it already showed for a scope with no batches. */}
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <TextField fullWidth size="small" label="Insured Name" variant="outlined"
                            value={insuredNameFilter}
                            onChange={(e) => setInsuredNameFilter(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                                '& .MuiInputBase-root': { marginTop: '1px !important' },
                                '& .MuiInputBase-input': { padding: '6px 14px !important' },
                            }}
                        />
                    </Grid>
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <TextField fullWidth size="small" label="Policy Number" variant="outlined"
                            value={policyNumberFilter}
                            onChange={(e) => setPolicyNumberFilter(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                                '& .MuiInputBase-root': { marginTop: '1px !important' },
                                '& .MuiInputBase-input': { padding: '6px 14px !important' },
                            }}
                        />
                    </Grid>

                    {/* BATCH DATE. Only days that actually HAVE a batch are offered — an open
                        calendar would make all 365 days a year look equally plausible and answer
                        "nothing found" for almost all of them, which reads as a broken filter
                        rather than an empty day. */}
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="batch-date-label" sx={{ transform: 'translate(14px, 8px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } }}>Batch Date</InputLabel>
                            <Select
                                labelId="batch-date-label"
                                label="Batch Date"
                                value={batchDateFilter}
                                onChange={(e: SelectChangeEvent) => {
                                    setSelectedRows([]);
                                    setBatchDownloadNotice(null);
                                    setBatchDateFilter(e.target.value);
                                }}
                                sx={{ '& .MuiSelect-select': { padding: '6px 14px !important' } }}
                            >
                                <MenuItem value="">All batch dates</MenuItem>
                                {batchDateOptions.map((d) => (
                                    <MenuItem key={d.batchDate} value={d.batchDate}>
                                        {formatFilingDate(d.batchDate)} ({d.policyCount})
                                    </MenuItem>
                                ))}
                                {/* Deliberately reachable. This is where every policy without
                                    usable Ready-to-File history lands, and a filter that could
                                    only ever HIDE those rows would make the screen look empty
                                    rather than incomplete. */}
                                <MenuItem value={NO_BATCH_DATE}>No batch date recorded</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ⚠️ SHOWN ONLY WHEN AN ORIGINAL ACTUALLY EXISTS. Selecting a batch day is
                        no longer enough: the lookup effect above has to have found retained
                        evidence for THIS Client + State + day. On staging and UAT that is never,
                        because automatic filing is off there and evidence is only written by a
                        real submission -- so the control simply is not offered, instead of being
                        offered and always failing. `originalArtifacts` is null while unknown, so
                        the button does not flicker in and out during the lookup. */}
                    {hasLoadedOnce && (batchDateFilter && batchDateFilter !== NO_BATCH_DATE
                        && originalArtifacts !== null && originalArtifacts.length > 0 && (
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <Tooltip title="Retrieves the ORIGINAL SUBMITTED file — the bytes actually transmitted to the state — and checks them against the SHA-256 recorded at the time. This is not the same as Download all, which BUILDS a proof batch from today's data and would fold in every correction made since. One file can cover several policies; it is returned once, not once per policy.">
                                <span>
                                    <Button fullWidth size="small" variant="outlined"
                                        disabled={downloadingBatch}
                                        onClick={handleDownloadOriginalBatch}
                                        sx={{ marginTop: '1px', textTransform: 'none' }}
                                    >
                                        {downloadingBatch ? 'Retrieving…' : 'Download original submitted file'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    ))}

                    {/* CLEAR (Josh QA). Last item in the filter row and pushed to the far right with
                        `ml: auto`, so it reads as an action on the filter bar rather than as another
                        filter. Matches the Clear control on State Reports and the Filing Queue:
                        outlined, medium, Close icon, the same slate border and hover.

                        Deliberately OUTSIDE the `hasLoadedOnce` gate above: if the very FIRST load
                        fails, the filer is left holding whatever filter caused it, and this is the
                        control that gets them back to a working view. Gating it on a batch having
                        landed would hide it in exactly the case it is most needed.

                        Resets FILTERS only. It does not touch filing status or data, Batch Date
                        derivation, Client Defaults, or any unrelated session state. */}
                    <Grid item xs={12} sm="auto" sx={{ ml: { sm: 'auto' }, display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={handleClearFilters}
                            disabled={!filtersAreActive}
                            startIcon={<Close />}
                            title="Resets Client, State, Batch Date, Insured Name, Policy Number and the status tab to their defaults."
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: '#e2e8f0',
                                color: '#475569',
                                '&:hover': { borderColor: '#2099d2', color: '#2099d2' },
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>

                </Grid>


                {batchDownloadNotice && (
                    <Alert severity={batchDownloadNotice.severity} sx={{ mt: 1, mb: 1 }}
                        onClose={() => setBatchDownloadNotice(null)}>
                        <Typography variant="body2">{batchDownloadNotice.message}</Typography>
                    </Alert>
                )}

                {policiesLoaded && policies.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                        <Typography>No extracted-and-sent policies found{(selectedPartner || selectedState) ? ' for this filter — clear the Client / State to see all.' : '.'}</Typography>
                    </Box>
                ) : (<>
                    {/* Summary is folded into the filing-status tab strip (see FilingStatusToolbar) — no
                        separate pill box, matching the decluttered "Proposed" layout. */}
                    {/* Full-bleed to match the Outstanding / Ready-to-File queues (Jerry punch-list #3):
                        the grid fills the shell's content width instead of sitting in a narrower inset
                        card. Only a light top border separates it from the toolbar. */}
                    {/* TRUNCATION IS NOT ALLOWED TO BE SILENT. The batch loads one page of
                        BATCH_FETCH_SIZE rows and pages client-side, so once the filter matches more than
                        that, the tab counts, the premium/tax totals and "Download all" describe only the
                        rows that were loaded — while reading as the whole book. Saying so is the minimum;
                        the real fix is server-side paging, which is a larger change than this warning. */}
                    {isTruncated && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {totalAvailable != null
                                ? `Showing the first ${loadedCount} of ${totalAvailable.toLocaleString()} matching policies.`
                                : `Showing the first ${loadedCount} matching policies, which is the maximum loaded at once.`}
                            {' '}The tab counts, premium and tax totals, and Download all cover only these
                            {' '}{loadedCount}. Narrow by Client or State to see a complete set.
                        </Alert>
                    )}
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ pt: 1, borderTop: '1px solid #e2e8f0' }}>
                            <Box sx={{ width: '100%' }}>
                                <DataGridPro
                                    autoHeight
                                    rows={visiblePolicies}
                                    columns={columns}
                                    getRowId={(row) => row.extractedDocumentDataId}
                                    checkboxSelection
                                    isRowSelectable={(params) => isRowInspectable(params.row as BatchReviewRow)}
                                    disableSelectionOnClick
                                    selectionModel={selectedRows}
                                    onSelectionModelChange={(newSelectionModel) => setSelectedRows(newSelectionModel as number[])}
                                    getRowHeight={getRowHeight}
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={getDetailPanelContent}
                                    detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                                    onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                                    // `components` names the (module-scope, stable) component; the state
                                    // it used to close over is handed to it through `componentsProps`.
                                    // v5 spells this `componentsProps.toolbar` — `slotProps` is the same
                                    // idea under its later name and does NOT exist in this version. The
                                    // object literal is rebuilt each render, which is only a re-render of
                                    // the toolbar; the component TYPE is what stays fixed, and that is
                                    // what stops the grid remounting the tab strip.
                                    components={{ Toolbar: FilingStatusToolbar }}
                                    componentsProps={{
                                        toolbar: {
                                            hasLoadedOnce,
                                            counts: lifecycleCounts,
                                            filingStatusFilter,
                                            setFilingStatusFilter,
                                            totals,
                                            policiesLoaded,
                                        } as FilingStatusToolbarProps,
                                    }}
                                    loading={loading}
                                    pagination
                                    page={page}
                                    pageSize={pageSize}
                                    onPageChange={(newPage) => setPage(newPage)}
                                    onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    sx={{
                                        // `autoHeight` with ZERO rows collapses the virtual scroller to
                                        // 0px in DataGrid v5. Two visible symptoms, both reported: the
                                        // "No rows" overlay is absolutely positioned inside that scroller
                                        // so it renders invisible, and the footer (Rows per page / count /
                                        // arrows) rides up into the middle of the card instead of sitting
                                        // under the grid. Giving the scroller a floor restores the normal
                                        // header -> body -> footer stack.
                                        //
                                        // ONLY when empty. Applied unconditionally it fights autoHeight
                                        // on small result sets — 1-3 rows would sit above 180px of dead
                                        // space, trading the empty-state bug for a worse and far more
                                        // common one.
                                        '& .MuiDataGrid-virtualScroller':
                                            visiblePolicies.length === 0 ? { minHeight: 180 } : {},

                                        // Footer baseline (Josh, 2026-07-30). "Rows per page:" and the
                                        // page-range text sat a couple of pixels below the `50` selector
                                        // and the arrows. MUI v5 renders BOTH of those labels as <p>, so
                                        // they inherit the theme's body paragraph margins (16px top and
                                        // bottom); the Select next to them is a <div> with none, so the
                                        // flex row centres three items of different heights and nothing
                                        // shares a baseline. Zeroing the two margins is the whole fix.
                                        //
                                        // Distinct from the empty-grid rule above: that one moved the
                                        // whole footer back under the grid when there were ZERO rows.
                                        // This misalignment is inside the footer and is present at every
                                        // row count, which is why it survived that fix.
                                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                            marginTop: 0,
                                            marginBottom: 0,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </>)}
            </Box>

            <DocumentViewer open={viewerOpen} documents={viewerDocs} onClose={() => setViewerOpen(false)} />

            <Dialog open={clientDefaultsOpen} onClose={() => setClientDefaultsOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <span>Client filing defaults{selectedPartner?.customerName ? ` — ${selectedPartner.customerName}` : ''}</span>
                    <Button variant="text"
                        onClick={() => { setClientDefaultsOpen(false); goToClientDefaults(); }}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#2099d2', fontSize: '13px', whiteSpace: 'nowrap' }}
                    >Open full admin ↗</Button>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedPartner && (
                        <ClientFilingDefaultsPanel coverageOnly token={token} partnersId={selectedPartner.partnersId} partnerName={selectedPartner.customerName} dense />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClientDefaultsOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm exactly what will be sent: the selected policies, per state, plus an explicit
                multi-client warning and a list of anything that will NOT be sent. The old dialog said
                "file N policies" while the handler filed every ready row in the batch. */}
            <Dialog open={fileBatchConfirmOpen} onClose={() => setFileBatchConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm filing</DialogTitle>
                <DialogContent dividers>
                    {filingPlan && (() => {
                        const plan = filingPlan;
                        const total = plan.groups.reduce((n, g) => n + g.rows.length, 0);
                        return (
                            <Box>
                                <Typography sx={{ fontSize: '14.5px', color: '#0f172a' }}>
                                    File <b>{total}</b> {total === 1 ? 'policy' : 'policies'} to <b>{plan.groups.length}</b> {plan.groups.length === 1 ? 'state' : 'states'} now?
                                </Typography>
                                <Box sx={{ mt: 1.5 }}>
                                    {plan.groups.map((g) => (
                                        <Box key={g.segment} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                                            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#334155' }}>{g.stateName}</Typography>
                                            <Typography sx={{ fontSize: '13.5px', color: '#5b6770' }}>{g.rows.length} {g.rows.length === 1 ? 'policy' : 'policies'}</Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {plan.clients.length > 1 && (
                                    <Box sx={{ mt: 2, p: 1.25, borderRadius: '8px', backgroundColor: '#fff4e0', border: '1px solid #f0d3a0' }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#b26a00' }}>
                                            This selection spans {plan.clients.length} clients
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', color: '#7a4a00', mt: 0.5 }}>
                                            {plan.clients.join(', ')}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12.5px', color: '#7a4a00', mt: 0.5 }}>
                                            Each client is its own filer. Confirm you really mean to file for all of them.
                                        </Typography>
                                    </Box>
                                )}

                                {plan.unresolved.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#c62828' }}>
                                            {plan.unresolved.length} selected {plan.unresolved.length === 1 ? 'policy' : 'policies'} will NOT be filed — no filing state
                                        </Typography>
                                        {plan.unresolved.map((r) => (
                                            <Typography key={r.extractedDocumentDataId} sx={{ fontSize: '12.5px', color: '#5b6770' }}>
                                                {describeRow(r)} — state {r.state || 'not set'}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {plan.duplicates.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#b26a00' }}>
                                            {plan.duplicates.length} checked {plan.duplicates.length === 1 ? 'row is another extraction' : 'rows are further extractions'} of a policy already in this batch
                                        </Typography>
                                        <Typography sx={{ fontSize: '12.5px', color: '#5b6770' }}>
                                            Each policy is submitted once, so {plan.duplicates.length === 1 ? 'it is' : 'they are'} not sent again:
                                        </Typography>
                                        {plan.duplicates.map((r) => (
                                            <Typography key={r.extractedDocumentDataId} sx={{ fontSize: '12.5px', color: '#5b6770' }}>
                                                {describeRow(r)}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => { setFileBatchConfirmOpen(false); setFilingPlan(null); }}
                        sx={{ textTransform: 'none', backgroundColor: '#c62828', color: WhiteColor, '&:hover': { backgroundColor: '#8e1c1c' } }}
                    >Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmFileBatch}
                        sx={{ textTransform: 'none', backgroundColor: '#2e7d32', color: WhiteColor, '&:hover': { backgroundColor: '#1b5e20' } }}
                    >File now</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/** Coverage code cell: hovering shows the human-readable coverage name (e.g. "1000" -> "Commercial
 *  Property") alongside any exception message, so a filer can eyeball the whole batch's codes without
 *  memorizing the 4-digit list. 
 */
function CoverageCodeCell({ row, value }: { row: BatchReviewRow; value: string | null }) {
    const issues = exceptionsForField(row, 'coverageCode');
    const tooltipParts = [row.coverageCodeName, ...issues.map((i) => i.message)].filter(Boolean);
    if (!tooltipParts.length) return <span>{value ?? ''}</span>;
    return (
        <Tooltip title={tooltipParts.join(' — ')}>
            <span>{value ?? '—'}</span>
        </Tooltip>
    );
};

/** Filing Status cell: the row's position in the filing LIFECYCLE (not ready → ready → awaiting state →
 *  filed, or rejected / error), so a filer can tell at a glance what still needs work, what is in
 *  flight with the state, and what is done. The required-fields meter is only meaningful before a
 *  submission exists, so it is dropped once one does — the confirmation number takes its place.
 *  Expand the "+" for the full Areas to Fix breakdown.
 */
function FilingStatusCell({ row }: { row: BatchReviewRow }) {
    const total = row.requiredTotal || 0;
    const readyCount = row.requiredReady || 0;
    const status: RowFilingStatus = rowFilingStatus(row);
    const chip = statusChipStyle(status);
    const ready = status === 'ready';
    const pct = total ? Math.round((readyCount / total) * 100) : 0;
    const meterColor = ready ? '#1e7e34' : '#94a3b8';
    const submittedLabel = row.submittedAt ? formatFilingDate(row.submittedAt) : '';
    // The reason the last attempt failed. A red chip that says only "Filing error" tells a filer nothing
    // about what to fix — and the answer ("Invalid SLIP User/Password specified.") was already being
    // captured, just never shown. Chip carries it as a tooltip; the row prints it so it is visible
    // without hovering, because the whole point is that it should not have to be hunted for.
    const reason = row.filingStatusReason ? String(row.filingStatusReason).trim() : '';
    // Hover copy for EVERY status (Kelly's checklist #3): the plain-language definition and what action
    // is required. When the clearinghouse also told us why, its words are appended BELOW the definition
    // rather than replacing it — the generic copy says what to do, the reason says what went wrong, and
    // a filer needs both. Previously the chip's tooltip was the reason alone, so the four statuses with
    // no reason (including "Pending State Approval", the one people ask about most) had no tooltip.
    const tip = statusTooltip(status);
    const chipTitle = reason ? `${tip}\n\nState's response: ${reason}` : tip;

    return (
        // TWO DELIBERATE LINES, not a wrapping row (Josh, 2026-07-31 — the Needs Attention rows were
        // "pretty squished together").
        //
        // This used to be ONE flex row with `flexWrap: 'wrap'` holding the chip AND the red reason. On a
        // Needs Attention row both are present, the column is not wide enough for them side by side, so
        // the reason wrapped onto a second line INSIDE A FIXED-HEIGHT ROW and collided with the rows
        // above and below — the chip label was visibly clipped mid-word across the row boundary.
        // Renaming rejected/error to the longer "Needs Attention" made the chip wider and brought the
        // wrap on sooner, so this is partly a regression from that change.
        //
        // The fix is to stop letting it wrap by accident and lay it out on purpose: chip (plus the
        // submission/meter detail) on line one, reason ellipsised to a single line underneath. The grid
        // gives these rows extra height via getRowHeight, so two lines fit instead of overflowing.
        // `minWidth: 0` on both the column and the row is what actually lets text-overflow work — a flex
        // child defaults to min-width:auto and refuses to shrink below its content, which is why the old
        // `maxWidth: 260` still overflowed in a narrower column.
        //
        // The reason stays PRINTED rather than moving into the tooltip alone: it was surfaced in the
        // first place so a filer would not have to hunt for it.
        <Box sx={{
            py: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            gap: 0.25, width: '100%', minWidth: 0,
        }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, maxWidth: '100%', minWidth: 0 }}>
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{chipTitle}</span>}>
                    <Chip
                        size="small"
                        icon={status === 'ready' || status === 'filed'
                            ? <CheckCircleOutline fontSize="small" />
                            : <ErrorOutline fontSize="small" />}
                        label={chip.label}
                        sx={{ backgroundColor: chip.bg, color: chip.fg, fontWeight: 700, flexShrink: 0 }}
                    />
                </Tooltip>
                {/* Line one only: the confirmation number sits beside the chip, never under it. */}
                {!chip.preFiling && (row.submissionNumber || submittedLabel) && (
                    <Tooltip title={`Submission ${row.submissionNumber ?? '—'}${submittedLabel ? ` · submitted ${submittedLabel}` : ''}`}>
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 700, color: '#5b6770', whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                        }}>
                            {row.submissionNumber ? `#${row.submissionNumber}` : ''}
                            {row.submissionNumber && submittedLabel ? ' · ' : ''}
                            {submittedLabel}
                        </Typography>
                    </Tooltip>
                )}
                {chip.preFiling && total > 0 && (
                    <Tooltip title={`${readyCount} of ${total} required fields have a value`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                            <Box sx={{ width: 46, height: 5, borderRadius: '3px', backgroundColor: '#e2e8f0', overflow: 'hidden' }}>
                                <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: meterColor, borderRadius: '3px' }} />
                            </Box>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5b6770' }}>{readyCount}/{total}</Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
            {/* Line two: the clearinghouse's own words, clipped to ONE line with an ellipsis. Full text
                stays available on hover and in the chip's tooltip, so nothing is lost by clipping. */}
            {reason && (
                <Tooltip title={reason}>
                    <Typography sx={{
                        fontSize: '11px', fontWeight: 600, color: '#c62828',
                        width: '100%', minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {reason}
                    </Typography>
                </Tooltip>
            )}
        </Box>
    );
};

/** The "+" expand-row panel — same "Not ready to file" / "Required fields" / "Areas to fix" /
 *  "Filing blockers" layout as the single-policy Filing Review screen, so a filer can see exactly why a
 *  batch row is flagged without leaving the grid. Missing fields are structurally field-keyed (readiness
 *  gaps); blockers are the state's own flat validation errors (report.errors) — real filing-stopping
 *  problems, distinct from the softer inline exceptions shown as cell highlights. 
 */
interface AreasToFixPanelProps {
    row: BatchReviewRow;
    token?: string;
    onFieldChange: (key: string, value: string) => void;
    /** Sync the coverage code back to the grid's flat Coverage column when picked here. */
    onCoverageChange: (code: string) => void;
    onViewDocument: (page?: number | null) => void;
    /** Build + download this policy's filing file (checklist #3). Returns when done/failed. */
    onDownloadBatch: () => Promise<void>;
    /** Re-run extraction for this policy (checklist #4), then the batch reloads. Returns when done. */
    onReExtract: () => Promise<void>;
    onSaved: () => void;
    /** True when this row's lazily-fetched detail failed to load — distinct from "still loading". */
    fieldsFailed?: boolean;
    /** Re-request this row's detail after a failure. */
    onRetryFields?: () => void;
}

function AreasToFixPanel({ row, token, onFieldChange, onCoverageChange, onViewDocument, onDownloadBatch, onReExtract, onSaved, fieldsFailed, onRetryFields }: AreasToFixPanelProps) {
    const allBlockers = row.blockers || [];
    const gaps = row.gaps || [];
    const allWarnings = row.warnings || [];
    const manualReview = row.manualReview || [];
    const lloydsFlags = manualReview.filter((f) => f.kind === 'lloyds_syndicate_confirm');
    const otherFlags = manualReview.filter((f) => f.kind !== 'lloyds_syndicate_confirm');
    // Lifecycle-aware: a policy that has been submitted is not "Ready to file", so the panel heading
    // must not claim it is.
    const ready = isRowFilable(row);
    // Resolved once: the heading below asks about it three times, and the colour is taken from the row
    // chip's own palette so a future palette change cannot make the panel disagree with the chip.
    const status = rowFilingStatus(row);
    // The chip's own label + colour, so the panel heading restates the row's status rather than
    // inventing a second vocabulary for it.
    const submittedChip = statusChipStyle(status);
    // Gate or record? See `isSubmittedStatus`. The findings below stay VISIBLE either way — they are
    // exactly what a filer needs in order to fix the policy for next time, and in practice they include
    // real config gaps like a missing FSLSO agency licence — only their voice changes.
    const isSubmitted = isSubmittedStatus(status);
    // One tone pair for every band that de-emphasises itself once the policy is on file, so the two
    // greyed bands cannot drift to different greys. `#cbd5e1` is this file's established neutral stripe.
    const bandTone = { stripe: '#cbd5e1', fg: '#475569' };
    const blockerCopy = blockerBandCopy(status);
    const lloydsCopy = lloydsConfirmCopy(status);

    // The Add/Edit Customer Policy Details page for this policy — where DB-sourced values (the writing
    // carrier / NAIC) are corrected, since they come from the policy record + carrier matrix, not the
    // documents. NOT where the SLA broker number is fixed (that's a Key-Vault credential).
    const policyPagePath = `${AppRoutes.admin.addEditCustomerPolicyDetails}/${row.customerPolicyId}`;
    // Split the "already submitted to the state" caution out of the plain warnings so it can STAND OUT
    // (it means a re-file over an existing submission — a filer should not miss it).
    //
    // On a manually-filed row it is no longer a caution, because the heading and the row chip already
    // say the policy is on file — so the amber band is dropped there. The note's TEXT is not: it can
    // carry detail the status alone does not (where the submission was seen, when, which record), and
    // silently dropping it would trade one contradiction for missing information. It falls back into
    // the neutral warnings band instead, stated once, calmly.
    const isAlreadySubmitted = (w: string) => /already .*submitted to the state|IsSubmittedToState/i.test(w);
    const showAlreadySubmittedAmberBand = showAlreadySubmittedBand(status);
    // filter, not find: the band pulls these OUT of `warnings` wholesale, so taking only the first one
    // would drop any second already-submitted note on the floor.
    const alreadySubmittedNotes = showAlreadySubmittedAmberBand
        ? allWarnings.filter(isAlreadySubmitted)
        : [];
    const warnings = showAlreadySubmittedAmberBand
        ? allWarnings.filter((w) => !isAlreadySubmitted(w))
        : allWarnings;
    // A blocker a filer fixes on the POLICY page (the DB carrier / NAIC), vs one they can't (the
    // Key-Vault SLA broker number, or an in-panel document field). Drives a "correct on policy page" link.
    const blockers = allBlockers;
    const hasPolicyFixableBlocker = allBlockers.some((b) => /writing insurer|carrier/i.test(b));
    const progressPct = row.requiredTotal ? Math.round((row.requiredReady / row.requiredTotal) * 100) : 0;

    // Per-policy Save (checklist #5): persist the filer's hand-keyed corrections back to this policy's
    // ExtractedDocumentData row. Sends every field's current value; the backend whitelists to the state's
    // editable EDD columns (reviewSaveColumns), so extra/read-only keys are ignored safely.
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [downloadBusy, setDownloadBusy] = useState(false);
    const [reExtractBusy, setReExtractBusy] = useState(false);
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    const doReExtract = async () => { setReExtractBusy(true); try { await onReExtract(); } finally { setReExtractBusy(false); } };
    const doDownload = async () => { setDownloadBusy(true); try { await onDownloadBatch(); } finally { setDownloadBusy(false); } };
    // THE PANEL'S OWN DRAFT of the editable values. Every keystroke used to go straight out through
    // `onFieldChange` into the page's `setPolicies`, i.e. a state update on the component that owns the
    // ENTIRE grid: one character typed into one field re-rendered every row, every cell renderer and
    // every open detail panel on the page. Typing in a long address on a 500-row batch was visibly
    // laggy for exactly that reason. Nothing outside this panel needs an unsaved keystroke, so the
    // draft is held here and pushed to the row only when the filer commits it with Save Corrections.
    // Collapsing (or cancelling) the panel unmounts this component, which discards the draft — the same
    // "unsaved edits are not kept" behaviour the Save button already implied.
    const snapshotFields = (fields: FilingField[]) => Object.fromEntries(fields.map((f) => [f.key, f.value]));
    const [draftFields, setDraftFields] = useState<FilingField[]>(() => row.fields || []);
    const [draftCoverageCode, setDraftCoverageCode] = useState(row.coverageCode);
    // Baseline snapshot to diff the draft against — Save Corrections is disabled whenever the current
    // values match this snapshot, including when a user edits a field and then edits it back to its
    // original value ("undo"). Captured with the draft, and re-captured with it, so the button always
    // measures "changed since the values this panel was seeded from".
    const [baseline, setBaseline] = useState(() => ({
        fields: snapshotFields(row.fields || []),
        coverageCode: row.coverageCode,
    }));
    // RE-SEED when the row's server-side values are replaced underneath us — which is a different event
    // from a keystroke now that keystrokes no longer touch the row. Two cases, both real: the panel
    // mounts BEFORE its detail has been lazily fetched (so the first seed is an empty field list, and
    // leaving it would make every populated field read as an edit against an empty baseline, enabling
    // Save on a row nobody touched), and a batch reload after a save / re-extract hands us a fresh row
    // object. `row.fields` is only ever a new array in those cases, so its identity is the signal.
    // Keyed on the row id too, because DataGridPro may reuse this component for a different row.
    // Assigning state during render is the documented way to reset derived state from props: React
    // re-runs this component immediately with the new state and never commits the stale pass.
    const seededFromRef = useRef<PanelSeed>({
        rowId: row.extractedDocumentDataId,
        fields: row.fields,
        coverageCode: row.coverageCode,
    });
    // ...EXCEPT while our own Save is in flight. handleSave commits the draft into the grid row with
    // `onFieldChange` BEFORE it posts, which hands us a new `row.fields` identity that is our own
    // optimistic write, not a server seed. Re-baselining on it would be silent data loss: if the POST
    // then FAILS, the edited values would already be the baseline, `hasChanges` would go false, and
    // "Save Corrections" would be disabled on a correction that never persisted — on a filing screen,
    // where the reviewer would have no way to tell. The ref makes the panel ignore the echo of its own
    // write; a genuine server reseed after a successful save still lands, because the flag is cleared
    // before the reload and the baseline is set explicitly from the draft on success.
    const savingRef = useRef(false);
    /** What `handleSave` last pushed into the row, by value -- lets the echo of our own write be told
     *  apart from genuinely new server values. Cleared the moment a render consumes it. */
    const optimisticRef = useRef<OptimisticWrite | null>(null);
    /** Bumped when a save settles, purely to force the re-seed guard to re-evaluate — see the finally
     *  block in `handleSave`. Never read for its value. */
    const [saveSettleNonce, setSaveSettleNonce] = useState(0);
    void saveSettleNonce;
    // The decision itself lives in helper/panelReseed.ts, with its own tests — this panel is not
    // exported and cannot be mounted on its own, and getting the "ignore our own write, but do not
    // swallow a refresh that arrives mid-save" rule wrong is silent, filing-visible data loss.
    const decision = nextPanelSeed(seededFromRef.current, row, savingRef.current, optimisticRef.current);
    if (decision.action === 'record') {
        // Our own optimistic write coming back. Record it so it cannot re-trigger, and leave the draft
        // and the baseline alone -- after a FAILED save this is what keeps `hasChanges` true, the
        // reviewer's text in the inputs and Save Corrections enabled on a correction that never
        // persisted.
        seededFromRef.current = decision.seed;
        optimisticRef.current = null;
    } else if (decision.action === 'reseed') {
        seededFromRef.current = decision.seed;
        optimisticRef.current = null;
        setDraftFields(row.fields || []);
        setDraftCoverageCode(row.coverageCode);
        setBaseline({ fields: snapshotFields(row.fields || []), coverageCode: row.coverageCode });
    }

    const hasChanges = draftFields.some((f) => (baseline.fields[f.key] ?? '') !== (f.value ?? ''))
        || (baseline.coverageCode ?? '') !== (draftCoverageCode ?? '');
    // Local only. `onFieldChange` / `onCoverageChange` are still the way the row is updated — they are
    // just called once, from handleSave, instead of once per keypress.
    const handleFieldEdit = (key: string, value: string) => {
        setDraftFields((prev) => prev.map((f) => (f.key === key ? { ...f, value } : f)));
    };
    const handleCoverageEdit = (key: string, code: string) => {
        setDraftFields((prev) => prev.map((f) => (f.key === key ? { ...f, value: code } : f)));
        setDraftCoverageCode(code);
    };
    const handleSave = async () => {
        // Commit the draft into the grid row BEFORE posting, which is the state the page was left in by
        // the old per-keystroke path at the moment Save was pressed — so the grid, the request and the
        // post-save reload all see exactly what they saw before. Only genuinely changed values are
        // pushed; an untouched field would be a no-op row update.
        savingRef.current = true;   // see the re-seed guard above: ignore the echo of our own write
        // ⚠️ THE try STARTS HERE, IMMEDIATELY AFTER THE FLAG IS RAISED, NOT AT THE POST. Everything
        // between the two — the optimistic row commits, the payload build, setSaveStatus — is code that
        // can throw, and anything thrown there would skip the `finally` and strand `savingRef` true for
        // the life of the panel, which is precisely the failure the flag's own comment promises cannot
        // happen: the re-seed guard would then ignore EVERY genuine server refresh.
        try {
            draftFields.forEach((f) => {
                if ((baseline.fields[f.key] ?? '') !== (f.value ?? '')) {
                    onFieldChange(f.key, f.value != null ? String(f.value) : '');
                }
            });
            if ((baseline.coverageCode ?? '') !== (draftCoverageCode ?? '')) {
                onCoverageChange(draftCoverageCode != null ? String(draftCoverageCode) : '');
            }
            // Remember, BY VALUE, exactly what we just pushed into the row. The row will come back to
            // us carrying these values and a fresh `fields` identity, and without this the re-seed
            // guard cannot tell that echo apart from real server data. Getting that wrong on the
            // FAILURE path re-baselines the panel onto edits that never persisted and disables Save.
            optimisticRef.current = {
                // Normalised the same way the comparison does it, so a null and an empty string are
                // not mistaken for a genuine server change.
                fields: Object.fromEntries(
                    draftFields.map((f) => [f.key, f.value == null ? '' : String(f.value)]),
                ),
                coverageCode: draftCoverageCode,
            };
            // Identical payload to before — same keys, same emptiness rules, same endpoint. The only change
            // is that the values are read out of the draft rather than out of the row they were mirrored to.
            const payloadFields: Record<string, string> = {};
            draftFields.forEach((f) => {
                // Address components are always sent — even when blanked — so clearing e.g. Apt/Suite persists
                // (the backend treats a blank component as "clear it"). Other fields skip empties (unchanged).
                if (ADDRESS_COMPONENT_KEYS.has(f.key)) payloadFields[f.key] = f.value != null ? String(f.value) : '';
                else if (f.value != null && f.value !== '') payloadFields[f.key] = String(f.value);
            });
            if (draftCoverageCode != null && draftCoverageCode !== '') payloadFields.coverageCode = String(draftCoverageCode);
            setSaveStatus('saving');
            const res: any = await apiClient('/AutomaticFiling/saveExtractionReview', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ extractedDocumentDataId: row.extractedDocumentDataId, state: row.state, fields: payloadFields }),
            });
            // `apiClient` returns `undefined` when fetch itself rejects, which is a FAILURE and not a
            // success — see helper/saveOutcome.ts for all three of its return shapes.
            const failed = isSaveFailure(res);
            setSaveStatus(failed ? 'error' : 'saved');
            if (failed) {
                // Baseline deliberately untouched, so `hasChanges` stays true and Save stays enabled with
                // the reviewer's text still in the inputs. A failed save must never look like a saved one.
                await Swal.fire({ icon: 'error', title: 'Save failed', text: res?.message || 'Could not save your corrections — please try again.', confirmButtonColor: '#2099d2' });
                return;
            }
            setBaseline({ fields: snapshotFields(draftFields), coverageCode: draftCoverageCode });
            // Re-review after saving. Blockers / the Ready verdict / the tab counts are computed server-side
            // in the review build on load; a save alone left them STALE, so a policy whose blockers were just
            // resolved still read "Not ready to file" until a manual reload. Reload the batch so the review
            // recomputes against the saved values — the row stays expanded (its id is unchanged by a save).
            onSaved();
            void Swal.fire({ icon: 'success', title: 'Corrections saved', text: 'Re-checking filing readiness…', timer: 1400, showConfirmButton: false });
        } finally {
            // Cleared here rather than immediately after the await so the flag also covers the error
            // modal: while the filer is reading "Save failed", a background reload must not overwrite
            // the text still sitting in the inputs. `onSaved()` is not awaited, so on the success path
            // this still runs before the reload's response can land.
            savingRef.current = false;
            // ⚠️ CLEARING A REF DOES NOT SCHEDULE A RENDER. The guard above defers its decision while a
            // save is in flight, deliberately recording nothing so a genuine refresh is re-evaluated
            // afterwards -- but "afterwards" only happens if something renders. On the failure path the
            // last state update (`setSaveStatus('error')`) happens BEFORE the modal is awaited, so
            // without this a real row refresh that landed during the modal would sit unprocessed until
            // some unrelated render, leaving the draft and baseline stale against the current row.
            setSaveSettleNonce((n) => n + 1);
        }
    };

    // Same state-specific scorecard filter the (retiring) FilingReview.tsx screen applies: only fields
    // this state actually requires for filing are shown/editable here — see the FIELD_DEFS `required`
    // flag added per-state on the backend.
    // The DRAFT, not `row.fields` — everything below (the inputs' values, the Lloyd's / NAIC derivations,
    // the grouping) has to reflect what the filer has typed but not yet saved.
    const fields = draftFields;
    const gapKeys = new Set(gaps.map((g) => g.key));
    // Field keys a hard blocker points at (backend-supplied). Unioned with the readiness gaps so ANY
    // missing required area — a counted gap OR a validator blocker on a DB/customer-policy field like
    // writing insurer / NAIC / SLA broker — outlines its field in red. State-generic: a field is a
    // "problem" if it's a required gap for this state OR a blocker names its key.
    const blockerKeys = new Set(row.blockerFieldKeys || []);
    const resolvedInsurerNaic = fields.find((f) => f.key === 'insurerNaic')?.value;
    const showWritingInsurerNaicDoc = lloydsFlags.length > 0 || resolvedInsurerNaic == null || resolvedInsurerNaic === '';
    // UMR only applies to a Lloyd's-of-London filing — hide it for every other carrier (Kelly, 2026-07-14).
    const isLloyds = isLloydsInsurer(fields, lloydsFlags.length > 0);
    const grouped = groupFilingFields(fields);

    // Group sub-header hints (the muted trailing text next to each .gt group title).
    const GROUP_HINT: Record<string, string> = {
        document: '(the only editable fields)',
        policyRecord: '(read-only)',
        computed: '',
        filer: '(read-only)',
    };
    const WIDE_FIELDS = ['descriptionOfRisk', 'physicalRiskAddress', 'mailingAddress', 'coverageLimits'];
    const stateLabel = (row.state || '').toUpperCase();

    // The per-field detail is fetched when the row is opened. Until it lands, render a spinner rather
    // than the full panel: with an empty field list every section would collapse and the panel would
    // claim this policy has no filing details and no gaps to fix, which is a different — and wrong —
    // statement from "still loading".
    if (!row.fieldsLoaded) {
        return (
            <Box sx={{ p: 1.5, backgroundColor: '#fafbfc' }}>
                <Box sx={{ backgroundColor: WhiteColor, border: '1px solid #e2e8f0', borderRadius: '12px', p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {fieldsFailed ? (
                        <>
                            <Typography sx={{ fontSize: '13px', color: '#c62828', fontWeight: 600 }}>
                                Could not load the filing details for this policy.
                            </Typography>
                            <Button size="small" variant="outlined" onClick={onRetryFields}
                                sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}>
                                Retry
                            </Button>
                        </>
                    ) : (
                        <>
                            <CircularProgress size={18} sx={{ color: '#2099d2' }} />
                            <Typography sx={{ fontSize: '13px', color: '#475569', fontWeight: 600 }}>Loading filing details…</Typography>
                        </>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1.5, backgroundColor: '#fafbfc' }}>
            {/* One rounded white card holds the verdict, the review bands, and the filing-details grid. */}
            <Box sx={{ backgroundColor: WhiteColor, border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                {/* Per-policy Save action bar (checklist #5) — persist hand-keyed corrections to this policy. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, px: 1.5, pt: 1.5, flexWrap: 'wrap' }}>
                    {saveStatus === 'saved' && <Typography sx={{ fontSize: '12.5px', color: '#1e7e34', fontWeight: 700 }}>Saved ✓</Typography>}
                    {saveStatus === 'error' && <Typography sx={{ fontSize: '12.5px', color: '#c62828', fontWeight: 700 }}>Save failed — try again</Typography>}
                    {/* Re-extract (checklist #4) + Download batch file (checklist #3) — per-policy actions. */}
                    {/* The evidence trail. Shown whenever this policy has ever been submitted — the
                        answer to "what did we actually send, and what did the state say" used to live
                        only on the clearinghouse's own website. */}
                    <Button size="small" variant="outlined" onClick={() => setEvidenceOpen(true)}
                        startIcon={<HistoryOutlined fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}>
                        Filing history
                    </Button>
                    <Button size="small" variant="outlined" onClick={doReExtract} disabled={reExtractBusy}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}>
                        {reExtractBusy ? 'Re-extracting…' : 'Re-extract'}
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={doDownload} disabled={downloadBusy}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: '#2099d2', color: '#2099d2' } }}>
                        {downloadBusy ? 'Building…' : 'Download batch file'}
                    </Button>
                    <Button size="small" variant="contained" onClick={handleSave} disabled={saveStatus === 'saving' || !hasChanges}
                        sx={FILING_QUEUE_BLUE_BUTTON_SX}>
                        {saveStatus === 'saving' ? 'Saving…' : 'Save Corrections'}
                    </Button>
                </Box>
                <FilingEvidenceDialog
                    open={evidenceOpen}
                    onClose={() => setEvidenceOpen(false)}
                    customerPolicyId={row.customerPolicyId}
                    policyNumber={row.policyNumber}
                    insuredName={row.insuredName}
                    token={token}
                />
                {/* detail-top: verdict (left) + required-fields block (right) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, p: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {/* A SUBMITTED row is NOT "not ready" — it is already with the state. It reads as
                            unready only because the completeness verdict (`ready`) is a pre-filing measure
                            and has nothing to say about a policy that has already gone. Every band below
                            now speaks in the past tense on such a row, so a red "Not ready to file" above
                            them would be the last voice still contradicting the rest — and it contradicts
                            the row's own chip too. Answer with the status instead, in the chip's own words
                            and colour, so the panel and the chip cannot disagree. */}
                        {isSubmitted
                            ? <CheckCircleOutline sx={{ color: submittedChip.fg, mt: '2px' }} />
                            : (ready ? <CheckCircleOutline sx={{ color: '#1e7e34', mt: '2px' }} /> : <ErrorOutline sx={{ color: '#c62828', mt: '2px' }} />)}
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: isSubmitted ? submittedChip.fg : (ready ? '#1e7e34' : '#c62828') }}>
                                {row.filingStatusReason
                                    ? 'Filing failed'
                                    : (isSubmitted ? submittedChip.label : (ready ? 'Ready to file' : 'Not ready to file'))}
                            </Typography>
                            {/* THE FILING failed — which is a different problem from the FIELDS being
                                incomplete, and saying "Not ready to file · 19/19" sent a filer to hunt
                                through fields that were already fine. A real case: the clearinghouse
                                said "Invalid SLIP User/Password specified." and the panel mentioned
                                neither the credential nor the failure. Lead with the state's own words. */}
                            {row.filingStatusReason && (
                                <Typography sx={{ fontSize: '12.5px', color: '#c62828', fontWeight: 600, maxWidth: 620 }}>
                                    {row.filingStatusReason}
                                </Typography>
                            )}
                            {/* One builder for both voices — a work list before filing, a record after.
                                See `reviewReasonLine`. */}
                            {(() => {
                                const line = reviewReasonLine(status, {
                                    requiredReady: row.requiredReady || 0,
                                    requiredTotal: row.requiredTotal || 0,
                                    blockers: blockers.length,
                                    manualReview: manualReview.length,
                                });
                                if (!line || (ready && !isSubmitted)) return null;
                                return <Typography sx={{ fontSize: '12px', color: '#5b6770' }}>{line}</Typography>;
                            })()}
                            {row.extractionDate && (
                                <Typography
                                    title={new Date(row.extractionDate).toLocaleString()}
                                    sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}
                                >
                                    Extracted {formatExtractedAgo(row.extractionDate)}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ minWidth: 240, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5b6770', letterSpacing: '0.05em' }}>
                                REQUIRED FIELDS{stateLabel ? ` · ${stateLabel}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#132D3D' }}>{row.requiredReady || 0} / {row.requiredTotal || 0}</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progressPct}
                            sx={{
                                height: 6, borderRadius: 3, backgroundColor: '#e2e8f0',
                                '& .MuiLinearProgress-bar': { backgroundColor: ready ? '#1e7e34' : '#94a3b8', borderRadius: 3 },
                            }}
                        />
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>
                            {row.requiredReady || 0} of {row.requiredTotal || 0} required fields have a value (document or database) — completeness only, not a guarantee the filing will pass.
                        </Typography>
                    </Box>
                </Box>

                {/* The missing required fields (gaps) are highlighted inline in red within FILING DETAILS
                    below — Kelly (2026-07-14): highlight only the specific field, and drop the duplicate
                    "Needs Review" banner that repeated the same fields here. Only genuine confirmations and
                    hard blockers get a calm callout, styled as a neutral row with a thin severity stripe
                    (no loud full-color fills) so the panel reads like the Update-Policy screen. */}
                {/* Same reframing as the blockers band below: a "confirm before filing" instruction cannot be
                    obeyed on a policy that is already filed. The finding stays, the imperative goes. */}
                {lloydsFlags.length > 0 && (
                    <Box sx={{
                        backgroundColor: '#fafbfc', borderTop: '1px solid #e2e8f0', p: 1.25,
                        borderLeft: `3px solid ${isSubmitted ? bandTone.stripe : '#e6c78c'}`,
                    }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isSubmitted ? bandTone.fg : '#8a6d3b', mb: 0.25 }}>
                            {lloydsCopy.heading}
                        </Typography>
                        {lloydsFlags.map((f, i) => (
                            <Typography key={i} sx={{ fontSize: '12px', color: '#5b6770' }}>
                                {f.reason}{lloydsCopy.note}
                            </Typography>
                        ))}
                    </Box>
                )}

                {otherFlags.map((f, i) => (
                    <Box key={i} sx={{ backgroundColor: '#fafbfc', borderTop: '1px solid #e2e8f0', borderLeft: '3px solid #cbd5e1', p: 1.25 }}>
                        <Typography sx={{ fontSize: '13px', color: '#334155' }}>
                            <strong>{kindLabel(f.kind)}</strong>{f.reason ? ` — ${f.reason}` : ''}
                        </Typography>
                        {/* The carrier/NAIC lives on the policy record (not the documents), so offer the jump to correct it there. */}
                        {f.kind === 'lloyds_naic_conflict' && <EditOnCustomerPageLink path={policyPagePath} />}
                    </Box>
                ))}

                {/* Red and imperative before filing, greyed and past-tense after — see `blockerBandCopy`. */}
                {blockers.length > 0 && (
                    <Box sx={{
                        backgroundColor: '#fafbfc', borderTop: '1px solid #e2e8f0', p: 1.25,
                        borderLeft: `3px solid ${isSubmitted ? bandTone.stripe : '#d32f2f'}`,
                    }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 0.25, color: isSubmitted ? bandTone.fg : '#c62828' }}>
                            {blockerCopy.heading}
                        </Typography>
                        {blockerCopy.note && (
                            <Typography sx={{ fontSize: '12px', color: '#5b6770', mb: 0.5 }}>{blockerCopy.note}</Typography>
                        )}
                        {blockers.map((message, i) => (
                            <Typography key={i} sx={{ fontSize: '12px', color: '#5b6770' }}>{message}</Typography>
                        ))}
                        {/* The carrier/NAIC come from the policy record — link there. (The SLA broker number is a
                            Key-Vault credential, not policy-editable, so it deliberately gets no policy-page link.) */}
                        {hasPolicyFixableBlocker && <EditOnCustomerPageLink path={policyPagePath} />}
                    </Box>
                )}

                {/* Dropped on a manually-filed row, where the heading and chip already say it — see
                    `showAlreadySubmittedBand`. The note's text falls back into the neutral warnings band. */}
                {alreadySubmittedNotes.length > 0 && (
                    <Box sx={{ backgroundColor: '#fff8e1', borderTop: '1px solid #e2e8f0', borderLeft: '3px solid #e6a700', p: 1.25 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#8a6d00', mb: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutline sx={{ fontSize: 16 }} /> Already submitted to the state
                        </Typography>
                        {alreadySubmittedNotes.map((note, i) => (
                            <Typography key={i} sx={{ fontSize: '12px', color: '#5b6770' }}>{note}</Typography>
                        ))}
                    </Box>
                )}

                {warnings.length > 0 && (
                    <Box sx={{ backgroundColor: '#fafbfc', borderTop: '1px solid #e2e8f0', borderLeft: '3px solid #cbd5e1', p: 1.25 }}>
                        {warnings.map((message, i) => (
                            <Typography key={i} sx={{ fontSize: '12px', color: '#5b6770' }}>{message}</Typography>
                        ))}
                    </Box>
                )}

                {/* FILING DETAILS (.frec): only the fields this state files (required !== false), grouped and
                    source-tagged, laid out as auto-fill grids so fields sit side-by-side. Document group is
                    editable (blue boxes); policy-record + computed are read-only; coverage is the one
                    editable computed field (searchable picker). */}
                <Box sx={{ borderTop: '1px solid #e2e8f0', p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography sx={{ fontSize: '11.5px', fontWeight: 800, color: '#132D3D', letterSpacing: '0.03em' }}>FILING DETAILS</Typography>
                        <Box component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>
                            — only fields this state files
                        </Box>
                    </Box>

                    {GROUP_ORDER.map((group) => {
                        // The structured address editors live in the (editable) document group. Computed
                        // BEFORE the field filter below, because whether the legacy COMBINED address field
                        // may be hidden depends on whether its structured replacement actually renders.
                        const showMailing = group === 'document' && hasAddressComponents(fields, MAILING_ADDRESS_KEYS);
                        const showRisk = group === 'document' && hasAddressComponents(fields, RISK_ADDRESS_KEYS);
                        // A legacy combined address key is suppressed ONLY when the structured editor that
                        // stands in for it is rendering. Suppressing it unconditionally made a REQUIRED field
                        // disappear with nothing in its place: SLIP+ states emit the structured MAILING
                        // components but NOT the structured RISK ones, so on Colorado — which REJ-requires a
                        // RiskAddress — `physicalRiskAddress` was filtered out here while `showRisk` stayed
                        // false. The result was a mandatory field with no input anywhere on the screen and a
                        // filing blocker no reviewer could clear by any route. Cancellations hit it hardest:
                        // the notice carries the mailing address, so extraction has no risk location to find.
                        const legacyHandledByEditor = (key: string) => (key === 'mailingAddress' ? showMailing : showRisk);
                        const groupFields = (grouped[group] || [])
                            .filter((f) => f.key !== 'writingInsurerNaicDoc' || showWritingInsurerNaicDoc)
                            .filter((f) => f.key !== 'umr' || isLloyds)
                            // Structured components are always rendered by the grouped AddressFieldsEditor.
                            .filter((f) => !ADDRESS_COMPONENT_KEYS.has(f.key))
                            .filter((f) => !(LEGACY_ADDRESS_KEYS.has(f.key) && legacyHandledByEditor(f.key)))
                            .filter((f) => f.required !== false);
                        if (!groupFields.length && !showMailing && !showRisk) return null;

                        return (
                            <Box key={group} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
                                {/* Source is conveyed by the section label itself (Extracted / Policy record /
                                    Computed / Licensee), so no DOC/DB/CALC badge is needed (Austin, 2026-07-15).
                                    The Policy-record header carries a backlink to the Add/Edit Customer Policy
                                    Details record so a filer can fix a wrong DB value (e.g. a $0.00 taxable fee)
                                    at the source and re-pull, instead of it being a dead read-only field. */}
                                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.02em' }}>
                                        {GROUP_LABELS[group] || group}
                                        {GROUP_HINT[group] ? <Box component="span" sx={{ fontWeight: 600, color: '#aab4bb', ml: 0.5 }}>{GROUP_HINT[group]}</Box> : null}
                                    </Typography>
                                    {group === 'policyRecord' && (
                                        <EditOnCustomerPageLink path={policyPagePath} inline
                                            label="Open policy record ↗"
                                            title="Open this policy's Add/Edit Customer Policy Details record — premium, taxable fees, writing carrier/NAIC and dates are corrected there (they come from the database, not the document), then re-pull." />
                                    )}
                                </Box>
                                {/* Mailing + (FL) risk address sit side-by-side in bounded columns rather than
                                    full-bleed, so the city field isn't a mile wide (Austin, 2026-07-15). */}
                                {(showMailing || showRisk) && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 1.5 }}>
                                        {showMailing && (
                                            <Box sx={{ flex: '1 1 320px', maxWidth: 460 }}>
                                                <AddressFieldsEditor label="Mailing address"
                                                    line1={addressComponent(fields, MAILING_ADDRESS_KEYS.line1)}
                                                    line2={addressComponent(fields, MAILING_ADDRESS_KEYS.line2)}
                                                    city={addressComponent(fields, MAILING_ADDRESS_KEYS.city)}
                                                    state={addressComponent(fields, MAILING_ADDRESS_KEYS.state)}
                                                    zip={addressComponent(fields, MAILING_ADDRESS_KEYS.zip)}
                                                    onChange={handleFieldEdit}
                                                    missing={gapKeys.has(MAILING_ADDRESS_KEYS.line1)} />
                                                {/* Where the address was read from + confidence — the structured
                                                    editor has no per-field chips, so surface one line for the whole
                                                    address from the enveloped mailing-address field. */}
                                                {(() => {
                                                    const pf = fields.find((x) => x.key === 'mailingAddress') || fields.find((x) => x.key === MAILING_ADDRESS_KEYS.line1);
                                                    return pf ? <FieldProvenance field={pf} onViewDocument={onViewDocument} /> : null;
                                                })()}
                                            </Box>
                                        )}
                                        {showRisk && (
                                            <Box sx={{ flex: '1 1 320px', maxWidth: 460 }}>
                                                <AddressFieldsEditor label={stateLabel === 'TX' ? 'Risk ZIP Code' : 'Physical risk address'}
                                                    line1={addressComponent(fields, RISK_ADDRESS_KEYS.line1)}
                                                    line2={addressComponent(fields, RISK_ADDRESS_KEYS.line2)}
                                                    city={addressComponent(fields, RISK_ADDRESS_KEYS.city)}
                                                    state={addressComponent(fields, RISK_ADDRESS_KEYS.state)}
                                                    zip={addressComponent(fields, RISK_ADDRESS_KEYS.zip)}
                                                    onChange={handleFieldEdit}
                                                    missing={gapKeys.has(RISK_ADDRESS_KEYS.line1)} />
                                                {(() => {
                                                    const pf = fields.find((x) => x.key === 'physicalRiskAddress') || fields.find((x) => x.key === RISK_ADDRESS_KEYS.line1);
                                                    return pf ? <FieldProvenance field={pf} onViewDocument={onViewDocument} /> : null;
                                                })()}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '12px 16px', alignItems: 'start' }}>
                                    {groupFields.map((f) => {
                                        // The coverage code is the one editable computed field (searchable picker).
                                        const isCoverage = group === 'computed' && isCoverageField(f);
                                        // "Admitted declinations" is a read-only summary (count + collapsible carrier list),
                                        // not an editable value — even though it sits in the editable document group.
                                        const isDeclinations = f.key === 'declinationCount';
                                        const value = f.value !== null && f.value !== undefined ? String(f.value) : '';
                                        // Red "required" ONLY for a hard-required field that is a real counted gap.
                                        // `required: 'conditional'` empty fields are optional confirm/override — never red.
                                        const isConditional = f.required === 'conditional';
                                        // Red for a required-and-empty field (this state's readiness gap)
                                        // OR any field a hard blocker names — the latter catches DB /
                                        // customer-policy fields (writing insurer, NAIC, SLA broker) that
                                        // aren't in the counted gaps. Generic across every state.
                                        const redMissing = (f.required === true && gapKeys.has(f.key)) || blockerKeys.has(f.key);
                                        // Address-like values wrap long; give them the full 2-col span so they don't crowd.
                                        const wide = WIDE_FIELDS.includes(f.key) || isCoverage || /address/i.test(f.key) || /address/i.test(f.label);
                                        return (
                                            <Box key={f.key} sx={{ minWidth: 0, gridColumn: wide ? { xs: 'auto', sm: 'span 2' } : 'auto' }}>
                                                {/* Source chip moved to the section header (Kelly, 2026-07-14) — the label stands alone here. */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, flexWrap: 'wrap' }}>
                                                    <Typography title={f.label} sx={{ fontSize: '11px', fontWeight: 700, color: '#5b6770', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{displayFieldLabel(f)}</Typography>
                                                </Box>
                                                {isCoverage ? (
                                                    <CoverageCodePicker
                                                        token={token}
                                                        state={row.state}
                                                        value={value}
                                                        missing={redMissing}
                                                        onChange={(code) => handleCoverageEdit(f.key, code)}
                                                    />
                                                ) : isDeclinations ? (
                                                    <>
                                                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', wordBreak: 'break-word' }}>
                                                            {value === '' ? '—' : formatFilingFieldValue(f)}
                                                        </Typography>
                                                        <DeclinationsSummary declinations={row.declinations || []} />
                                                    </>
                                                ) : f.editable ? (
                                                    <EditableFilingField field={f}
                                                        value={value}
                                                        missing={redMissing}
                                                        conditional={isConditional}
                                                        onChange={handleFieldEdit}
                                                        isLloydsNaicField={f.key === 'writingInsurerNaicDoc' && lloydsFlags.length > 0}
                                                    />
                                                ) : (
                                                    <>
                                                        {/* Read-only field: when it's a missing/blocked required area, give it the SAME
                                                            red outline the editable fields get (border + tinted fill), so "this needs
                                                            fixing" reads identically whether the fix is here or on the customer page. */}
                                                        <Box sx={redMissing ? {
                                                            border: '1px solid #d32f2f', borderRadius: '6px',
                                                            backgroundColor: 'rgba(211,47,47,0.06)', padding: '6px 9px',
                                                        } : undefined}>
                                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: redMissing ? '#c0392b' : '#334155', wordBreak: 'break-word' }}>
                                                                {value === '' ? (redMissing ? '— required (resolve upstream)' : '—') : formatFilingFieldValue(f)}
                                                            </Typography>
                                                        </Box>
                                                        {/* DB-sourced field the backend flags resolveUpstream (writing-insurer NAIC, agency
                                                            license) that's missing OR flagged red by a blocker (e.g. a present-but-invalid value)
                                                            → link to the customer page where it's corrected, instead of editing it in place. Gated
                                                            on the explicit backend flag (not a group heuristic) so the link only appears on the
                                                            fields actually owned upstream (Kelly, 2026-07-15). */}
                                                        {f.resolveUpstream === true && (value === '' || redMissing) && (
                                                            /** App path to this policy's Add/Edit Customer Policy Details page (e.g.
                                                             *  `/Admin/AddEditCustomerPolicyDetails/124194`). Rendered as an "edit on customer page" link next to
                                                             *  DB-sourced review fields (writing-insurer NAIC, agency license) that are missing / "resolve
                                                             *  upstream" — those are corrected at the source, not editable in the filing area (Kelly, 2026-07-15). */
                                                            <Box><EditOnCustomerPageLink path={`${AppRoutes.admin.addEditCustomerPolicyDetails}/${row.customerPolicyId}`} /></Box>
                                                        )}
                                                    </>
                                                )}
                                                <FieldProvenance field={f} onViewDocument={onViewDocument} />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};
